"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react"

export type Language = "en" | "ar"

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  isRTL: boolean
  t: (key: string, interpolations?: { [key: string]: string | number }) => string
  dir: "ltr" | "rtl"
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

interface LanguageProviderProps {
  children: ReactNode
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check localStorage first, then browser language, default to English
    const savedLang = localStorage.getItem("language") as Language
    if (savedLang && (savedLang === "en" || savedLang === "ar")) {
      return savedLang
    }

    // Check browser language
    const browserLang = navigator.language.toLowerCase()
    if (browserLang.startsWith("ar")) {
      return "ar"
    }

    return "en"
  })

  const isRTL = language === "ar"
  const dir = isRTL ? "rtl" : "ltr"

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    localStorage.setItem("language", lang)

    // Update document direction and language
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr"
    document.documentElement.lang = lang

    // Add/remove RTL class for global styling
    if (lang === "ar") {
      document.body.classList.add("rtl")
      document.body.classList.remove("ltr")
    } else {
      document.body.classList.add("ltr")
      document.body.classList.remove("rtl")
    }
  }

  // Translation function
  const t = (key: string, interpolations?: { [key: string]: string | number }): string => {
    const translations = language === "ar" ? arTranslations : enTranslations
    let translation = translations[key] || key

    // Handle interpolations
    if (interpolations) {
      Object.keys(interpolations).forEach((placeholder) => {
        translation = translation.replace(`{${placeholder}}`, String(interpolations[placeholder]))
      })
    }

    return translation
  }

  useEffect(() => {
    // Set initial document direction and language
    setLanguage(language)
  }, [language]) // Re-run when language changes

  const value: LanguageContextType = {
    language,
    setLanguage,
    isRTL,
    t,
    dir,
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

// English translations
const enTranslations: Record<string, string> = {
  // Common
  "loading": "Loading...",
  "error": "Error",
  "success": "Success",
  "cancel": "Cancel",
  "save": "Save",
  "delete": "Delete",
  "edit": "Edit",
  "add": "Add",
  "search": "Search",
  "filter": "Filter",
  "sort": "Sort",
  "refresh": "Refresh",
  "back": "Back",
  "next": "Next",
  "previous": "Previous",
  "submit": "Submit",
  "close": "Close",
  "open": "Open",
  "yes": "Yes",
  "no": "No",
  "confirm": "Confirm",
  "warning": "Warning",
  "info": "Information",
  "selectLanguage": "Select Language",
  "viewMenu": "View Menu",
  "welcome": "Welcome",

  // Navigation
  "dashboard": "Dashboard",
  "orders": "Orders",
  "restaurants": "Restaurants",
  "users": "Users",
  "user": "User",
  "analytics": "Analytics",
  "settings": "Settings",
  "profile": "Profile",
  "logout": "Logout",
  "login": "Login",
  "register": "Register",

  // Restaurant Portal
  "restaurantPortal": "Restaurant Portal",
  "signInToManage": "Sign in to manage your restaurant",
  "emailAddress": "Email Address",
  "password": "Password",
  "enterYourEmail": "Enter your email",
  "enterYourPassword": "Enter your password",
  "rememberMe": "Remember me",
  "forgotPassword": "Forgot password?",
  "signIn": "Sign In",
  "newToPlatform": "New to the platform?",
  "registerRestaurant": "Register your restaurant",
  "backToMainSite": "← Back to main site",
  "loginSuccessful": "Login successful! Redirecting...",
  "loginFailed": "Login failed. Please try again.",
  "checkingAuthentication": "Checking authentication...",

  // Landing Page
  "turboPlatform": "Turbo Platform",
  "restaurantLogin": "Restaurant Login",
  "getStarted": "Get Started",
  "adminAccess": "Admin Access",
  "theComplete": "The Complete",
  "foodDeliveryPlatform": "Food Delivery Platform",
  "streamlineDescription": "Streamline your food delivery operations with our advanced platform",
  "startYourRestaurant": "Start Your Restaurant",
  "threePowerfulApplications": "Three Powerful Applications",
  "seamlessSolutions": "Seamless solutions for restaurants, customers, and drivers — all in one platform",
  "completeRestaurantManagement": "Complete restaurant management system with advanced POS dashboard, table QR ordering, and comprehensive analytics for informed decision-making.",
  "advancedPOSDashboard": "Advanced POS Dashboard",
  "tableQROrderPlacement": "Table QR Order Placement",
  "directDeliveryOrderIntegration": "Direct Delivery Order Integration",
  "revenuePerformanceReports": "Revenue & Performance Reports",
  "turboDriverApp": "Turbo Driver App",
  "mobileApplicationForDrivers": "Mobile application for delivery drivers to accept and manage orders, with delivery confirmation via unique codes and real-time navigation.",
  "acceptManageOrders": "Accept & Manage Orders",
  "deliveryConfirmationCodes": "Delivery Confirmation Codes",
  "realTimeNavigation": "Real-time Navigation",
  "earningsTracking": "Earnings Tracking",
  "turboDeliveryApp": "Turbo Delivery App",
  "customerFacingMobileApp": "Customer-facing mobile app for browsing restaurant menus, placing orders, and tracking deliveries in real-time with seamless user experience.",
  "browseRestaurantMenus": "Browse Restaurant Menus",
  "easyOrderPlacement": "Easy Order Placement",
  "liveDeliveryTracking": "Live Delivery Tracking",
  "seamlessUserExperience": "Seamless User Experience",
  "getItFrom": "Get it from",
  "googlePlay": "Google Play",
  "appStore": "App Store",
  "comingSoon": "Coming Soon",
  "whyChooseTurboPlatform": "Why Choose Turbo Platform?",
  "builtForModernBusinesses": "Built for modern food delivery businesses with cutting-edge technology",
  "lightningFast": "Lightning Fast",
  "realTimeOrderProcessing": "Real-time order processing and instant updates",
  "smartAnalytics": "Smart Analytics",
  "dataDrivenInsights": "Data-driven insights to grow your business",
  "crossPlatform": "Cross-Platform",
  "worksSeamlessly": "Works seamlessly on all devices and platforms",
  "support247": "24/7 Support",
  "expertAssistance": "Expert assistance whenever you need it",
  "readyToTransform": "Ready to Transform Your Food Delivery Business?",
  "joinThousands": "Join thousands of restaurants already using Turbo Platform to grow their business",
  "startFreeTrial": "Start Free Trial",
  "completeFoodDeliveryPlatform": "The complete food delivery platform for modern businesses.",
  "platform": "Platform",
  "driverApp": "Driver App",
  "deliveryApp": "Delivery App",
  "helpCenter": "Help Center",
  "connect": "Connect",
  "contactUs": "Contact Us",
  "privacyPolicy": "Privacy Policy",
  "termsOfService": "Terms of Service",
  "allRightsReserved": "© 2024 Turbo Platform. All rights reserved.",

  // Language Switcher
  "language": "Language",
  "english": "English",
  "arabic": "العربية",
  "switchLanguage": "Switch Language",

  // Admin Panel
  "turboDeliveryAdmin": "Turbo Delivery Admin",
  "adminPanel": "Admin Panel",
  "adminDashboard": "Admin Dashboard",
  "manageDeliveryPlatform": "Manage your delivery platform",
  "totalRevenue": "Total Revenue",
  "totalOrders": "Total Orders",
  "activeUsers": "Active Users",
  "activeCaptains": "Active Captains",
  "recentOrders": "Recent Orders",
  "noRecentOrders": "No recent orders",
  "orderNumber": "Order #{number}",
  "welcomeBack": "Welcome back! Here's what's happening with your delivery platform.",
  "loadingDashboardData": "Loading dashboard data...",

  // Restaurant Portal
  "restaurantDashboard": "Restaurant Dashboard",
  "manageRestaurantOperations": "Manage your restaurant operations",
  "loadingRestaurantDashboard": "Loading restaurant dashboard...",
  "noDataAvailable": "No data available",
  "todaysOrders": "Today's Orders",
  "pendingOrders": "Pending Orders",
  "foodItems": "Food Items",
  "tables": "Tables",
  "todaysRevenue": "Today's Revenue",
  "connected": "Connected",
  "disconnected": "Disconnected",
  "restaurantName": "Restaurant Name",

  // Navigation & Menu Items
  "captains": "Captains",
  "promotions": "Promotions",
  "feedback": "Feedback",
  "notifications": "Notifications",
  "paymentMethods": "Payment Methods",
  "staffManagement": "Staff Management",
  "posSystem": "POS System",
  "posOrders": "POS Orders",
  "deliveryOrders": "Delivery Orders",
  "foodPerformance": "Food Performance",
  "reports": "Reports",

  // Common Actions
  "update": "Update",
  "create": "Create",
  "remove": "Remove",
  "select": "Select",
  "choose": "Choose",
  "browse": "Browse",
  "view": "View",
  "details": "Details",

  // Status & Messages
  "status": "Status",
  "pending": "Pending",
  "confirmed": "Confirmed",
  "preparing": "Preparing",
  "ready": "Ready",
  "delivered": "Delivered",
  "cancelled": "Cancelled",
  "completed": "Completed",
  "active": "Active",
  "inactive": "Inactive",
  "online": "Online",
  "offline": "Offline",

  // Time & Dates
  "today": "Today",
  "yesterday": "Yesterday",
  "thisWeek": "This Week",
  "thisMonth": "This Month",
  "lastMonth": "Last Month",
  "thisYear": "This Year",
  "lastYear": "Last Year",
  "date": "Date",
  "time": "Time",
  "createdAt": "Created At",
  "updatedAt": "Updated At",

  // Financial
  "revenue": "Revenue",
  "cost": "Cost",
  "profit": "Profit",
  "total": "Total",
  "amount": "Amount",
  "price": "Price",
  "discount": "Discount",
  "tax": "Tax",
  "subtotal": "Subtotal",
  "grandTotal": "Grand Total",

  // Analytics & Reports
  "overview": "Overview",
  "summary": "Summary",
  "trends": "Trends",
  "performance": "Performance",
  "statistics": "Statistics",
  "metrics": "Metrics",
  "insights": "Insights",
  "breakdown": "Breakdown",
  "comparison": "Comparison",
  "period": "Period",
  "dateRange": "Date Range",
  "customRange": "Custom Range",
  "last7Days": "Last 7 Days",
  "last30Days": "Last 30 Days",
  "last90Days": "Last 90 Days",

  // Food & Menu
  "menu": "Menu",
  "menuItem": "Menu Item",
  "category": "Category",
  "description": "Description",
  "ingredients": "Ingredients",
  "allergens": "Allergens",
  "nutritionalInfo": "Nutritional Information",
  "availability": "Availability",
  "available": "Available",
  "unavailable": "Unavailable",
  "outOfStock": "Out of Stock",
  "inStock": "In Stock",

  // Tables & Seating
  "table": "Table",
  "capacity": "Capacity",
  "location": "Location",
  "notes": "Notes",
  "qrCode": "QR Code",
  "generateQR": "Generate QR",
  "downloadQR": "Download QR",
  "tableStatus": "Table Status",
  "tableAvailable": "Available",
  "occupied": "Occupied",
  "reserved": "Reserved",
  "maintenance": "Maintenance",

  // Users & Staff
  "staff": "Staff",
  "employee": "Employee",
  "role": "Role",
  "permissions": "Permissions",
  "accessLevel": "Access Level",
  "admin": "Admin",
  "manager": "Manager",
  "cashier": "Cashier",
  "waiter": "Waiter",
  "kitchen": "Kitchen",

  // Forms & Inputs
  "name": "Name",
  "fullName": "Full Name",
  "username": "Username",
  "email": "Email",
  "phone": "Phone",
  "phoneNumber": "Phone Number",
  "address": "Address",
  "city": "City",
  "state": "State",
  "country": "Country",
  "postalCode": "Postal Code",
  "zipCode": "ZIP Code",
  "confirmPassword": "Confirm Password",
  "currentPassword": "Current Password",
  "newPassword": "New Password",

  // Actions & Buttons
  "download": "Download",
  "upload": "Upload",
  "export": "Export",
  "import": "Import",
  "print": "Print",
  "share": "Share",
  "copy": "Copy",
  "paste": "Paste",

  // Empty States
  "noData": "No data available",
  "noResults": "No results found",
  "noItems": "No items to display",
  "emptyState": "Nothing to see here",
  "tryAgain": "Try again",
  "refreshPage": "Refresh page",

  // Loading States
  "processing": "Processing...",
  "saving": "Saving...",
  "updating": "Updating...",
  "deleting": "Deleting...",
  "connecting": "Connecting...",
  "signingIn": "Signing in...",

  // Success Messages
  "savedSuccessfully": "Saved successfully",
  "updatedSuccessfully": "Updated successfully",
  "deletedSuccessfully": "Deleted successfully",
  "createdSuccessfully": "Created successfully",
  "operationSuccessful": "Operation completed successfully",

  // Error Messages
  "somethingWentWrong": "Something went wrong",
  "tryAgainLater": "Please try again later",
  "operationFailed": "Operation failed",
  "validationError": "Please check your input",
  "requiredField": "This field is required",
  "invalidFormat": "Invalid format",
  "connectionError": "Connection error",

  // Confirmation Dialogs
  "areYouSure": "Are you sure?",
  "thisActionCannotBeUndone": "This action cannot be undone",
  "confirmDelete": "Confirm deletion",
  "confirmAction": "Confirm action",
  "proceed": "Proceed",
  "abort": "Abort",

  // Restaurant Registration
  "registerYourRestaurant": "Register Your Restaurant",
  "joinTurboPlatform": "Join Turbo Platform and grow your food delivery business",
  "restaurantInformation": "Restaurant Information",
  "ownerAccountInformation": "Owner Account Information",
  "enterRestaurantName": "Enter restaurant name",
  "describeRestaurant": "Describe your restaurant and cuisine",
  "cuisineCategory": "Cuisine Category",
  "selectCategory": "Select category",
  "restaurantPhone": "Restaurant Phone",
  "restaurantPhonePlaceholder": "Restaurant phone number",
  "restaurantEmail": "Restaurant Email",
  "restaurantEmailPlaceholder": "Restaurant email",
  "streetAddress": "Street Address",
  "streetAddressPlaceholder": "Street address",
  "zipCodePlaceholder": "ZIP code",
  "deliveryFee": "Delivery Fee ($)",
  "minimumOrder": "Minimum Order ($)",
  "estimatedDeliveryTime": "Estimated Delivery Time",
  "estimatedTimePlaceholder": "e.g., 30-45 min",
  "deliveryRadius": "Delivery Radius (km)",
  "enterFullName": "Enter your full name",
  "enterEmail": "Enter your email",
  "enterPhoneNumber": "Enter your phone number",
  "enterPassword": "Enter your password",
  "confirmYourPassword": "Confirm your password",
  "nextOwnerInformation": "Next: Owner Information",
  "backToRestaurantInfo": "Back to Restaurant Info",
  "createRestaurantAccount": "Create Restaurant & Account",
  "creatingAccount": "Creating Account...",
  "alreadyHaveAccount": "Already have an account?",
  "signInHere": "Sign in here",
  "passwordsDoNotMatch": "Passwords do not match",
  "passwordMinLength": "Password must be at least 6 characters long",
  "fillAllFields": "Please fill in all restaurant information fields",
  "registrationSuccessful": "Restaurant and account created successfully! Redirecting to login...",
  "registrationFailed": "Registration failed. Please try again.",

  // Food Categories
  "fastFood": "Fast Food",
  "pizza": "Pizza",
  "asian": "Asian",
  "middleEastern": "Middle Eastern",
  "desserts": "Desserts",
  "healthy": "Healthy",
  "coffee": "Coffee",
  "appetizer": "Appetizer",
  "mainDish": "Main Dish",
  "sideDish": "Side Dish",
  "sandwich": "Sandwich",
  "beverage": "Beverage",
  "drink": "Drink",

  // Additional Food Items Page Translations
  "manageFoodItems": "Manage your restaurant's menu items",
  "searchFoodItems": "Search food items...",
  "allItems": "All Items",
  "itemsCount": "{count} of {total} items",
  "noFoodItemsFound": "No food items found",
  "tryAdjustingFilters": "Try adjusting your filters",
  "getStartedAddingFood": "Get started by adding your first food item",
  "deleteFoodItemConfirm": "Are you sure you want to delete this food item?",
  "isFeatured": "Featured",
  "isPopular": "Popular",
  "originalPrice": "Original Price",
  "calories": "Calories",
  "protein": "Protein",
  "carbs": "Carbs",
  "fat": "Fat",

  // Additional Tables Page Translations
  "manageRestaurantTables": "Manage your restaurant's dining tables",
  "addTable": "Add Table",
  "totalTables": "Total Tables",
  "allLocations": "All Locations",
  "tablesCount": "{count} of {total} tables",
  "tableNumber": "Table {number}",
  "people": "people",
  "quickStatus": "Quick Status",
  "noTablesFound": "No tables found",
  "getStartedAddingTable": "Get started by adding your first table",
  "deleteTableConfirm": "Are you sure you want to delete this table?",
  "downloadQRCode": "Download QR Code",
  "editTable": "Edit Table",
  "deleteTable": "Delete Table",
  "addNewTable": "Add New Table",
  "updateTableInfo": "Update table information",
  "createNewTableDescription": "Create a new table for your restaurant",
  "tableNumberLabel": "Table Number",
  "notesOptional": "Notes (Optional)",
  "indoor": "Indoor",
  "outdoor": "Outdoor",
  "private": "Private",
  "updateTable": "Update Table",
  "createTable": "Create Table",

  // Table Order Page Translations
  "loadingMenu": "Loading menu...",
  "unableToLoadTable": "Unable to load table information. Please check the QR code.",
  "addedToCart": "Added to cart",
  "missingInformation": "Missing Information",
  "pleaseProvideNameAndPhone": "Please provide your name and phone number",
  "emptyCart": "Empty Cart",
  "pleaseAddItems": "Please add items to your order",
  "orderPlacedSuccessfully": "Order Placed Successfully!",
  "failedToPlaceOrder": "Failed to place order. Please try again.",
  "invalidQRCode": "Invalid QR Code",
  "tableNotAvailable": "This table QR code is not valid or the table is not available.",
  "goHome": "Go Home",
  "addToOrder": "Add to Order",
  "tapToViewDetails": "Tap to view details",
  "selectSize": "Select Size",
  "inCart": "in cart",
  "addMore": "Add More",
  "customerReviews": "Customer Reviews",
  "noReviewsYet": "No reviews yet",
  "beFirstToReview": "Be the first to review this item!",
  "noItemsInCategory": "No items available in this category",
  "viewCart": "View Cart",
  "yourOrder": "Your Order",
  "yourInformation": "Your Information",
  "enterYourName": "Enter your name",
  "placingOrder": "Placing Order...",
  "placeOrder": "Place Order",
  "checkOrderStatus": "Check Order Status",
  "enterConfirmationCode": "Enter your confirmation code to track your order",
  "confirmationCodePlaceholder": "Enter code",
  "checking": "Checking...",
  "checkStatus": "Check Status",
  "loadingOrderStatus": "Loading order status...",
  "orderNotFound": "Order Not Found",
  "orderNotFoundDescription": "The order with this confirmation code was not found.",
  "statusUpdated": "Status Updated",
  "orderStatusRefreshed": "Order status has been refreshed",
  "unableToFindTable": "Unable to find table information",
  "orderPlaced": "Order Placed",
  "served": "Served",
  "yourOrderIsBeingPrepared": "Your order is being prepared by our kitchen",
  "yourOrderIsReady": "Your order is ready to be served!",
  "enjoyYourMeal": "Enjoy your meal!",
  "waitingForConfirmation": "Waiting for restaurant confirmation",
  "orderConfirmedPreparing": "Order confirmed, preparing soon",
  "orderItemsDetails": "Order Details",
  "confirmationCode": "Confirmation Code",
  "orderTime": "Order Time",
  "orderMore": "Order More",
  "autoRefreshNote": "Status updates automatically every 30 seconds",
  "tableOccupied": "Table Occupied",
  "tableOccupiedByAnother": "This table is currently being used by another customer. Please ask a staff member for assistance.",

  // Rating translations
  "howWasYourMeal": "How was your meal?",
  "ratingHelpsUsImprove": "Your feedback helps us improve our service",
  "overallExperience": "Overall Experience",
  "serviceQuality": "Service Quality",
  "rateYourItems": "Rate Your Items",
  "additionalComments": "Additional Comments",
  "shareYourThoughts": "Share your thoughts about your experience...",
  "maybeLater": "Maybe Later",
  "submitRating": "Submit Rating",
  "submitting": "Submitting...",
  "thankYouForRating": "Thank you for your feedback!",
  "yourFeedbackHelps": "Your feedback helps us serve you better",
  "pleaseProvideOverallRating": "Please provide an overall rating",
  "failedToSubmitRating": "Failed to submit rating. Please try again.",
  "rateOrder": "Rate Order",
  "optional": "optional",

  // Close Table & Receipt
  "closeTable": "Close Table",
  "closing": "Closing...",
  "tableClosedSuccessfully": "Table closed successfully",
  "failedToCloseTable": "Failed to close table. Please try again.",
  "thankYouForVisit": "Thank You for Your Visit!",
  "hopeToSeeYouAgain": "We hope to see you again soon",
  "sessionEndedMessage": "Your table session has ended. You can start a new order anytime!",
  "redirectingIn": "Redirecting in",
  "seconds": "seconds",
  "startNewOrder": "Start New Order",
  "downloadReceipt": "Download Receipt",
  "receiptDownloaded": "Receipt downloaded successfully",
  "failedToDownloadReceipt": "Failed to download receipt. Please try again.",

  // Additional Orders Page Translations
  "manageTrackOrders": "Manage and track restaurant orders",
  "searchOrders": "Search orders...",
  "pickedUp": "Picked Up",
  "onTheWay": "On The Way",
  "allTypes": "All Types",
  "dineIn": "Dine-in",
  "newestFirst": "Newest First",
  "oldestFirst": "Oldest First",
  "highestTotal": "Highest Total",
  "lowestTotal": "Lowest Total",
  "ordersCount": "{count} of {total} orders",
  "items": "items",
  "viewDetails": "View Details",
  "startPreparing": "Start Preparing",
  "markReady": "Mark Ready",
  "outForDelivery": "Out for Delivery",
  "pickedUpStatus": "Picked Up",
  "servedStatus": "Served",
  "deliveredStatus": "Delivered",
  "cancelOrder": "Cancel Order",
  "noOrdersFound": "No orders found",
  "ordersWillAppear": "Orders will appear here when customers place them",
  "orderDetails": "Order #{number} Details",
  "customerInformation": "Customer Information",
  "orderType": "Order Type",
  "deliveryAddress": "Delivery Address",
  "tableInformation": "Table Information",
  "orderItems": "Order Items",
  "quantity": "Qty",
  "pricing": "Pricing",
  "specialInstructions": "Special Instructions",
  "updateStatus": "Update Status",
  "confirmOrder": "Confirm Order",
  "customizations": "Customizations",
  "dineInOrder": "Dine-in Order",
  "unknownItem": "Unknown Item",

  // Analytics Page Translations
  "analyticsInsights": "Analytics and insights for your restaurant",
  "salesTrends": "Sales Trends",
  "popularItems": "Popular Items",
  "customerInsights": "Customer Insights",
  "timeAnalysis": "Time Analysis",
  "orderVolume": "Order Volume",
  "avgOrderValue": "Average Order Value",
  "customerSatisfaction": "Customer Satisfaction",
  "peakHours": "Peak Hours",
  "revenueGrowth": "Revenue Growth",
  "orderTrends": "Order Trends",
  "monthlyRevenue": "Monthly Revenue",
  "dailyOrders": "Daily Orders",
  "customerRetention": "Customer Retention",
  "deliveryPerformance": "Delivery Performance",

  // Food Performance Page Translations
  "foodItemAnalytics": "Food item analytics and performance metrics",
  "bestSellers": "Best Sellers",
  "lowPerformers": "Low Performers",
  "profitMargins": "Profit Margins",
  "costAnalysis": "Cost Analysis",
  "inventoryTurnover": "Inventory Turnover",
  "seasonalTrends": "Seasonal Trends",
  "categoryPerformance": "Category Performance",
  "menuOptimization": "Menu Optimization",
  "itemProfitability": "Item Profitability",
  "revenuePerformance": "Revenue Performance",
  "quantityPerformance": "Quantity Performance",
  "rating": "Rating",
  "orderPerformance": "Order Performance",
  "updateStaffMember": "Update Staff Member",
  "salesVelocity": "Sales Velocity",
  "menuEngineering": "Menu Engineering",
  "foodCosts": "Food Costs",
  "marginAnalysis": "Margin Analysis",

  // Reports Page Translations
  "generateCustomReports": "Generate and export custom reports",
  "salesReports": "Sales Reports",
  "financialReports": "Financial Reports",
  "inventoryReports": "Inventory Reports",
  "customerReports": "Customer Reports",
  "operationalReports": "Operational Reports",
  "dailyReport": "Daily Report",
  "weeklyReport": "Weekly Report",
  "monthlyReport": "Monthly Report",
  "customReport": "Custom Report",
  "exportReport": "Export Report",
  "generateReport": "Generate Report",
  "reportPeriod": "Report Period",
  "startDate": "Start Date",
  "endDate": "End Date",
  "reportType": "Report Type",
  "reportFormat": "Report Format",
  "csvFormat": "CSV",
  "pdfFormat": "PDF",
  "excelFormat": "Excel",

  // POS System Page Translations
  "pointOfSaleSystem": "Point of sale system for in-restaurant orders",
  "newOrder": "New Order",
  "currentOrders": "Current Orders",
  "quickActions": "Quick Actions",
  "cashRegister": "Cash Register",
  "cash": "Cash",
  "card": "Card",
  "digitalWallet": "Digital Wallet",
  "splitBill": "Split Bill",
  "addDiscount": "Add Discount",
  "addTip": "Add Tip",
  "voidItem": "Void Item",
  "receipt": "Receipt",
  "emailReceipt": "Email Receipt",
  "customerDisplay": "Customer Display",
  "kitchenDisplay": "Kitchen Display",

  // POS Orders Page Translations
  "pointOfSaleOrders": "Point of sale orders and transactions",
  "tableOrders": "Table Orders",
  "walkInOrders": "Walk-in Orders",
  "takeawayOrders": "Takeaway Orders",
  "takeaway": "Takeaway",
  "orderQueue": "Order Queue",
  "completedOrders": "Completed Orders",
  "voidedOrders": "Voided Orders",
  "refundedOrders": "Refunded Orders",
  "transactionHistory": "Transaction History",
  "orderTimeline": "Order Timeline",
  "kitchenTickets": "Kitchen Tickets",
  "receiptHistory": "Receipt History",
  "dailyClosing": "Daily Closing",
  "shiftReports": "Shift Reports",

  // Staff Management Page Translations
  "manageRestaurantStaff": "Manage your restaurant staff and permissions",
  "addStaff": "Add Staff",
  "staffList": "Staff List",
  "workSchedule": "Work Schedule",
  "timeTracking": "Time Tracking",
  "payroll": "Payroll",
  "employeeProfile": "Employee Profile",
  "clockIn": "Clock In",
  "clockOut": "Clock Out",
  "break": "Break",
  "shift": "Shift",
  "overtime": "Overtime",
  "attendance": "Attendance",
  "leaves": "Leaves",
  "holidays": "Holidays",

  // Additional Settings Page Translations
  "restaurantSettings": "Restaurant settings and preferences",
  "generalSettings": "General Settings",
  "operationHours": "Operation Hours",
  "deliverySettings": "Delivery Settings",
  "paymentSettings": "Payment Settings",
  "notificationSettings": "Notification Settings",
  "taxSettings": "Tax Settings",
  "menuSettings": "Menu Settings",
  "tableSettings": "Table Settings",
  "kitchenSettings": "Kitchen Settings",
  "reportSettings": "Report Settings",
  "integrationSettings": "Integration Settings",
  "backupSettings": "Backup Settings",
  "systemSettings": "System Settings",
  "advancedSettings": "Advanced Settings",
  "saveSettings": "Save Settings",
  "resetSettings": "Reset Settings",
  "importSettings": "Import Settings",
  "exportSettings": "Export Settings",
  "appearancePreferences": "Appearance & Preferences",
  "customizeYourInterfacePreferences": "Customize your interface preferences",
  "appearancePreferencesDescription": "Adjust language and theme settings to personalize your experience",
  "selectYourPreferredLanguage": "Select your preferred language",
  "switchBetweenLightAndDarkMode": "Switch between light and dark mode",
  "theme": "Theme",
  "light": "Light",
  "dark": "Dark",
  "serviceFeeSettings": "Service Fee Settings",
  "configureServiceFeeSettingsForRestaurant": "Configure service fee settings for your restaurant",
  "configureServiceFeeSettingsDescription": "Set up service fees that will be applied to all orders. Service fees are typically used to cover operational costs.",
  "enableServiceFee": "Enable Service Fee",
  "applyServiceFeeToAllOrders": "Apply service fee to all orders",
  "serviceFeePercentage": "Service Fee Percentage",
  "setServiceFeeRatePercentage": "Set the service fee rate as a percentage",
  "serviceFeeEnabled": "Service fee enabled",
  "serviceFeeDisabled": "Service fee disabled",
  "serviceFeePercentageUpdated": "Service fee percentage updated to {percentage}%",
  "failedToUpdateServiceFee": "Failed to update service fee setting",
  "failedToUpdateServiceFeePercentage": "Failed to update service fee percentage",
  "serviceFeeConfigurationActive": "Service Fee Configuration Active",
  "serviceFeeWillBeAppliedAt": "Service fee will be applied at {percentage}%",

  // Restaurant Dashboard - Additional Translations
  "topSellingItems": "Top Selling Items (7 days)",
  "sold": "sold",
  "noSalesData": "No sales data available",
  "revenueTrend": "Revenue Trend (7 days)",
  "noRevenueData": "No revenue data available",
  "hourlyPerformance": "Hourly Performance",
  "avgPrepTime": "Avg Prep Time",
  "min": "min",
  "completionRate": "completion rate",
  "needAttention": "Need attention",
  "guest": "Guest",

  // Profile Page
  "myProfile": "My Profile",
  "manageAccountSettings": "Manage your account settings and preferences",
  "personalInformation": "Personal Information",
  "securitySettings": "Security Settings",
  "accountInformation": "Account Information",
  "bio": "Bio",
  "tellUsAboutYourself": "Tell us about yourself",
  "enterCurrentPassword": "Enter current password",
  "enterNewPassword": "Enter new password",
  "confirmNewPassword": "Confirm new password",
  "changePassword": "Change Password",
  "saveChanges": "Save Changes",
  "profileUpdatedSuccessfully": "Profile updated successfully",
  "profileUpdateFailed": "Failed to update profile",
  "profileImageUpdated": "Profile image updated successfully",
  "imageUploadFailed": "Failed to upload image",
  "pleaseSelectImage": "Please select an image file",
  "imageTooLarge": "Image is too large. Maximum size is 5MB",
  "passwordChangedSuccessfully": "Password changed successfully",
  "passwordChangeFailed": "Failed to change password",
  "memberSince": "Member Since",
  "accountType": "Account Type",
  "administrator": "Administrator",
  "ownerInformation": "Owner Information",
  "manageRestaurantProfile": "Manage your restaurant profile and settings",
  "restaurantOwner": "Restaurant Owner",

  // Loading Messages
  "loadingPOSDashboard": "Loading POS dashboard...",
  "loadingPOSOrders": "Loading POS orders...",
  "loadingPOSSystem": "Loading POS system...",
  "loadingSettings": "Loading settings...",
  "loadingStaffData": "Loading staff data...",
  "loadingAnalytics": "Loading analytics...",
  "loadingPerformanceData": "Loading performance data...",
  "loadingReports": "Loading reports...",
  "loadingTables": "Loading tables...",
  "loadingOrders": "Loading orders...",

  // Error Messages
  "failedToLoadPOSDashboard": "Failed to load POS dashboard data",
  "failedToLoadPOSOrders": "Failed to load POS orders",
  "failedToLoadSettings": "Failed to load settings",
  "failedToLoadStaff": "Failed to load staff members",
  "failedToUpdateStatus": "Failed to update status",
  "failedToUpdatePayment": "Failed to update payment status",
  "failedToCreateOrder": "Failed to create POS order",
  "failedToLoadTables": "Failed to load tables",
  "failedToLoadOrders": "Failed to load orders",
  "failedToLoadFoodItems": "Failed to load food items",
  "failedToLoadPaymentMethods": "Failed to load payment methods",
  "failedToSaveSettings": "Failed to save settings",
  "failedToUpdateTax": "Failed to update tax setting",
  "unableToLoadTables": "Unable to load tables",

  // POS-Specific
  "posDashboard": "POS Dashboard",
  "posSystemOverview": "Point of Sale System Overview",
  "activeCashiers": "Active Cashiers",
  "availableTables": "Available Tables",
  "performanceOverview": "Performance Overview",
  "posTransactionsToday": "POS transactions today",
  "totalSalesToday": "Total sales today",
  "staffOnDuty": "Staff on duty",
  "readyForCustomers": "Ready for customers",
  "ordersWaitingToBeProcessed": "Orders waiting to be processed",

  // Settings-Specific
  "orderSettings": "Order Settings",
  "autoAcceptOrders": "Auto-accept Orders",
  "orderNotifications": "Order Notifications",
  "lowStockAlerts": "Low Stock Alerts",
  "analyticsTracking": "Analytics Tracking",
  "autoArchiveOrders": "Auto-archive Orders",
  "customFee": "Custom Fee",
  "taxPercentage": "Tax Percentage",

  // Tables-Specific
  "tableManagement": "Table Management",
  "manageTables": "Manage your restaurant tables",
  "tableCapacity": "Capacity",
  "tableLocation": "Location",

  // Orders-Specific
  "orderStatus": "Order Status",
  "delivery": "Delivery",
  "pickup": "Pickup",
  "customerInfo": "Customer Information",
  "totalAmount": "Total Amount",
  "markAsPaid": "Mark as Paid",
  "printOrder": "Print Order",

  // Profile
  "restaurantProfile": "Restaurant Profile",
  "loadingProfileData": "Loading profile data...",
  "logoUpdatedSuccessfully": "Logo updated successfully",
  "logoUpdateFailed": "Failed to update logo",
  "failedToLoadRestaurantData": "Failed to load restaurant data",

  // Performance & Reports
  "performanceMetrics": "Performance Metrics",
  "topPerformingItems": "Top Performing Items",
  "lowPerformingItems": "Low Performing Items",
  "downloadReport": "Download Report",
  "averageOrderValue": "Average Order Value",
  "revenuePerCashier": "Revenue per Cashier",
  "latestTransactions": "Latest point of sale transactions",
  "cashPayments": "Cash Payments",
  "cardPayments": "Card Payments",
  "ofTotalRevenue": "of total revenue",
  "vsYesterday": "vs yesterday",
  "growth": "Growth",
  "manageInStoreOrders": "Manage in-store orders and transactions",
  "orderSummary": "Order Summary",
  "orderDiscount": "Order Discount",
  "seats": "seats",
  "noItemsInCart": "No items in cart",

  // Staff Management
  "manageRestaurantStaffMembers": "Manage restaurant staff members and permissions",
  "addStaffMember": "Add Staff Member",
  "totalStaff": "Total Staff",
  "allTeamMembers": "All team members",
  "activeStaff": "Active Staff",
  "currentlyActive": "Currently active",
  "managers": "Managers",
  "adminAndManagerRoles": "Admin & Manager roles",
  "deactivatedAccounts": "Deactivated accounts",
  "filtersAndSearch": "Filters & Search",
  "filterAndSearchStaffMembers": "Filter and search staff members",
  "searchStaffMembers": "Search staff members...",
  "filterByRole": "Filter by role",
  "filterByStatus": "Filter by status",
  "allRoles": "All roles",
  "allStatus": "All status",
  "applyFilters": "Apply Filters",
  "staffMembers": "Staff Members",
  "ofStaffMembers": "of {total} staff members",
  "noStaffMembersFound": "No staff members found",
  "tryAdjustingYourFilters": "Try adjusting your filters",
  "getStartedByAddingFirstStaffMember": "Get started by adding your first staff member",
  "editStaffMember": "Edit Staff Member",
  "addNewStaffMember": "Add New Staff Member",
  "firstName": "First Name",
  "lastName": "Last Name",
  "kitchenStaff": "Kitchen Staff",
  "staffMemberActivated": "Staff member activated successfully",
  "staffMemberDeactivated": "Staff member deactivated successfully",
  "staffMemberDeleted": "Staff member deleted successfully",
  "staffMemberUpdated": "Staff member updated successfully",
  "staffMemberCreated": "Staff member created successfully",
  "areYouSureDeleteStaffMember": "Are you sure you want to delete this staff member?",
  "joined": "Joined",
  "lastLogin": "Last login",
  "canProcessOrders": "Can Process Orders",
  "canManageInventory": "Can Manage Inventory",
  "canManageUsers": "Can Manage Users",
  "canViewReports": "Can View Reports",
  "canManageTables": "Can Manage Tables",
  "canManageMenu": "Can Manage Menu",
  "canProcessRefunds": "Can Process Refunds",
  "canViewAnalytics": "Can View Analytics",
  "accessDenied": "Access Denied",
  "insufficientPermissions": "You don't have sufficient permissions to access this page.",
  "contactAdministratorForAccess": "Please contact your administrator to request access.",
  "requiredPermissions": "Required permissions",
  "activePermissions": "active",
  "passwordRequired": "Password is required",
  "passwordUpdatedSuccessfully": "Password updated successfully",
  "failedToUpdatePassword": "Failed to update password",
  "updatePassword": "Update Password",
  "staffLogin": "Staff Login",
  "signInAsStaffMember": "Sign in as a staff member",
  "restaurantId": "Restaurant ID",
  "enterRestaurantId": "Enter restaurant ID",
  "loginAsOwner": "Login as Owner",
  "staffMember": "Staff Member",
  "loginAsStaff": "Login as Staff",
  "notSet": "Not set",
  "editProfile": "Edit Profile",
  "manageYourPersonalInformation": "Manage your personal information and account settings",

  // Kitchen Orders Page
  "kitchenOrders": "Kitchen Orders",
  "kitchenOrdersDescription": "Manage and prepare confirmed orders",
  "waitingToStart": "Waiting to Start",
  "currentlyPreparing": "Currently Preparing",
  "noOrdersInKitchen": "No orders in kitchen",
  "orderStatusUpdated": "Order status updated",
  "totalItems": "Total Items",
  "activeOrders": "Active Orders",
  "ordersRequiringAttention": "Orders requiring your attention",

  // Waiter Orders Page
  "waiterOrders": "Waiter Orders",
  "waiterOrdersDescription": "Serve ready orders to customers",
  "readyToServe": "Ready to Serve",
  "noReadyOrders": "No ready orders",
  "readyOrdersWillAppear": "Ready orders will appear here",
  "markServed": "Mark Served",
  "returnOrder": "Return Order",
  "returnReason": "Return Reason",
  "enterReturnReason": "Enter the reason for returning this order",
  "confirmReturn": "Confirm Return",
  "orderReturned": "Order returned successfully",
  "orderServed": "Order marked as served",
  "pleaseProvideReturnReason": "Please provide a reason for returning the order",
  "minutesAgo": "minutes ago",
  "hoursAgo": "hours ago",
  "more": "more",
  "returned": "Returned",

  // POS New Order Page
  "tapToSelectSize": "Tap to select size",
  "tapToAdd": "Tap to add",
  "searchTableNumber": "Search table number...",
  "noItemsFound": "No items found",
  "noItemsSelected": "No items selected",
  "selected": "Selected",
  "posOrderCreatedSuccessfully": "POS order created successfully",
  "newPOSOrder": "New POS Order",
  "createNewPOSOrder": "Create a new POS order",
  "backToPOS": "Back to POS",
  "selectTable": "Select Table",
  "noTablesAvailable": "No tables available",
  "ofItems": "of {count} items",
  "viewTableOrders": "View Table Orders",
  "customerDetails": "Customer Details",
  "customerName": "Customer Name",

  // Delivery Orders Page
  "manageDeliveryOrders": "Manage all delivery orders",
  "fromTurboApp": "From Turbo App",
  "allDeliverySources": "All Sources",
  "appOrder": "App Order",
  "webOrder": "Web Order",
  "loadingDeliveryOrders": "Loading delivery orders...",
  "failedToLoadDeliveryOrders": "Failed to load delivery orders",
  "noDeliveryOrdersFound": "No delivery orders found",
  "deliveryOrdersWillAppear": "Delivery orders from Turbo app will appear here",
  "totalDeliveryOrders": "Total Delivery Orders",
  "fromDeliveryOrders": "From delivery orders",
  "driver": "Driver",
  "noAddress": "No address provided",
  "customer": "Customer",

  // Customer Management Page
  "customers": "Customers",
  "manageCustomers": "Manage customers and their information",
  "totalCustomers": "Total Customers",
  "activeCustomers": "Active Customers",
  "phoneOrderCustomers": "Phone Order Customers",
  "appCustomers": "App Customers",
  "quickPhoneSearch": "Quick Phone Search",
  "searchCustomers": "Search customers...",
  "allSources": "All Sources",
  "mobileApp": "Mobile App",
  "phoneOrder": "Phone Order",
  "web": "Web",
  "totalSpent": "Total Spent",
  "lastOrder": "Last Order",
  "never": "Never",
  "showingOf": "Showing {count} of {total}",
  "customerNotFound": "Customer Not Found",
  "noCustomerWithPhone": "No customer found with this phone number",
  "searchFailed": "Search failed",
  "noCustomersFound": "No customers found",
  "failedToLoadCustomers": "Failed to load customers",

  // Customer Detail Modal
  "registrationSource": "Registration Source",
  "adminNotes": "Admin Notes",
  "addNotesAboutCustomer": "Add notes about this customer...",
  "noNotesYet": "No notes yet",
  "notesUpdated": "Notes updated",
  "failedToUpdateNotes": "Failed to update notes",
  "noSavedAddresses": "No saved addresses",
  "setDefault": "Set Default",
  "addressDeleted": "Address deleted",
  "failedToDeleteAddress": "Failed to delete address",
  "defaultAddressUpdated": "Default address updated",
  "failedToUpdateAddress": "Failed to update address",
  "noOrderHistory": "No order history",
  "unknownRestaurant": "Unknown Restaurant",
  "favoriteRestaurants": "Favorite Restaurants",
  "noFavoriteRestaurants": "No favorite restaurants",
  "favoriteFoodItems": "Favorite Food Items",
  "noFavoriteFoodItems": "No favorite food items",

  // Live Order Map
  "liveOrderMap": "Live Order Map",
  "failedToLoadMapData": "Failed to load map data",
  "googleMapsApiKeyMissing": "Google Maps API key is missing",
  "failedToLoadGoogleMaps": "Failed to load Google Maps",
  "exitFullscreen": "Exit Fullscreen",
  "fullscreen": "Fullscreen",

  // Phone Order Wizard
  "newPhoneOrder": "New Phone Order",
  "step": "Step",
  "of": "of",
  "customerPhone": "Customer Phone",
  "enterValidPhone": "Please enter a valid phone number",
  "existingCustomer": "Existing Customer",
  "newCustomer": "New Customer",
  "enterCustomerName": "Enter customer name",
  "selectAddress": "Select Address",
  "useSavedAddress": "Use Saved Address",
  "addNewAddress": "Add New Address",
  "home": "Home",
  "work": "Work",
  "other": "Other",
  "fullAddressOptional": "Full address (optional)",
  "deliveryNotes": "Delivery notes (optional)",
  "searchRestaurants": "Search restaurants...",
  "all": "All",
  "cart": "Cart",
  "assignDriver": "Assign Driver",
  "autoAssign": "Auto Assign",
  "systemWillAssign": "System will assign available driver",
  "anySpecialInstructions": "Any special instructions for this order?",
  "orderCreatedSuccessfully": "Order created successfully",
  "dropPinOnMap": "Drop pin on map to set delivery location",
  "loadingMap": "Loading map...",
  "locationSelected": "Location selected",
  "clickOrDragMarker": "Click on map or drag the marker to set location",
  "noCoordinates": "No coordinates - select on map",
  "addressFromMap": "Address (auto-filled from map)",

  // Additional Admin Delivery Orders
  "captainAssigned": "Captain assigned successfully",
  "failedToAssignCaptain": "Failed to assign captain",
  "noAvailableDrivers": "No available drivers",
  "order": "Order",
  "actions": "Actions",
  "awaitingDriver": "Awaiting driver",
  "note": "Note",
  "successfullyDelivered": "Successfully delivered",
  "awaitingConfirmation": "Awaiting confirmation",

  // Session Expiry
  "sessionExpired": "Session Expired",
  "sessionExpiredDesc": "Your session and token have expired. Please log in again to continue using the application securely.",
  "logInAgain": "Log In Again",

  // Menu Book
  "menuBook": "Menu Book",
  "browseRestaurants": "Browse Restaurants",
  "searchRestaurants": "Search restaurants...",
  "allCategories": "All",
  "currentlyOpen": "Open",
  "currentlyClosed": "Closed",
  "viewRestaurantMenu": "View Menu",
  "noRestaurantsFound": "No restaurants found",
  "tryDifferentSearch": "Try a different search or category",
  "restaurantMenu": "Menu",
  "operatingHoursTitle": "Operating Hours",
  "deliveryMinimum": "Delivery Minimum",
  "contactInfo": "Contact Information",
  "restaurantInfo": "Restaurant Info",
  "itemUnavailable": "Unavailable",
  "allItems": "All",
  "featuredItems": "Featured",
  "popularItems": "Popular",
  "noMenuItems": "No menu items available",
  "noItemsInCategory": "No items in this category",
  "prepTime": "Prep Time",
  "ingredients": "Ingredients",
  "allergens": "Allergens",
  "nutritionalInfo": "Nutritional Info",
  "calories": "Calories",
  "protein": "Protein",
  "carbs": "Carbs",
  "fat": "Fat",
  "poweredByTurbo": "Powered by Turbo Delivery",
  "monday": "Monday",
  "tuesday": "Tuesday",
  "wednesday": "Wednesday",
  "thursday": "Thursday",
  "friday": "Friday",
  "saturday": "Saturday",
  "sunday": "Sunday",
  "today": "Today",
  "closed": "Closed",
  "fastFood": "Fast Food",
  "pizza": "Pizza",
  "asian": "Asian",
  "middleEastern": "Middle Eastern",
  "desserts": "Desserts",
  "healthy": "Healthy",
  "coffee": "Coffee",
  "appetizer": "Appetizer",
  "mainDish": "Main Dish",
  "sideDish": "Side Dish",
  "sandwich": "Sandwich",
  "beverage": "Beverage",
  "drink": "Drink",
}

// Arabic translations
const arTranslations: Record<string, string> = {
  // Common
  "loading": "جاري التحميل...",
  "error": "خطأ",
  "success": "نجح",
  "cancel": "إلغاء",
  "save": "حفظ",
  "delete": "حذف",
  "edit": "تعديل",
  "add": "إضافة",
  "search": "بحث",
  "filter": "تصفية",
  "sort": "ترتيب",
  "refresh": "تحديث",
  "back": "رجوع",
  "next": "التالي",
  "previous": "السابق",
  "submit": "إرسال",
  "close": "إغلاق",
  "open": "فتح",
  "yes": "نعم",
  "no": "لا",
  "confirm": "تأكيد",
  "warning": "تحذير",
  "info": "معلومات",
  "selectLanguage": "اختر اللغة",
  "viewMenu": "عرض القائمة",
  "welcome": "مرحباً",

  // Navigation
  "dashboard": "لوحة التحكم",
  "orders": "الطلبات",
  "restaurants": "المطاعم",
  "foodItems": "الأطعمة",
  "captains": "السائقون",
  "users": "المستخدمين",
  "user": "المستخدم",
  "promotions": "العروض الترويجية",
  "feedback": "التقييمات",
  "notifications": "الإشعارات",
  "analytics": "التحليلات",
  "paymentMethods": "طرق الدفع",
  "settings": "الإعدادات",
  "profile": "الملف الشخصي",
  "logout": "تسجيل الخروج",
  "login": "تسجيل الدخول",
  "register": "تسجيل",

  // Restaurant Portal
  "restaurantPortal": "بوابة المطعم",
  "signInToManage": "سجل دخولك لإدارة مطعمك",
  "emailAddress": "عنوان البريد الإلكتروني",
  "password": "كلمة المرور",
  "enterYourEmail": "أدخل بريدك الإلكتروني",
  "enterYourPassword": "أدخل كلمة المرور",
  "rememberMe": "تذكرني",
  "forgotPassword": "نسيت كلمة المرور؟",
  "signIn": "دخول",
  "newToPlatform": "جديد على المنصة؟",
  "registerRestaurant": "سجل مطعمك",
  "backToMainSite": "← العودة للموقع الرئيسي",
  "loginSuccessful": "تم تسجيل الدخول بنجاح! جاري إعادة التوجيه...",
  "loginFailed": "فشل تسجيل الدخول. يرجى المحاولة مرة أخرى.",
  "checkingAuthentication": "جاري التحقق من المصادقة...",
  // Admin Panel
  "turboDeliveryAdmin": "إدارة توربو ديليفري",
  "adminPanel": "لوحة المدير",
  "adminDashboard": "لوحة تحكم المدير",
  "manageDeliveryPlatform": "إدارة منصة التوصيل الخاصة بك",
  "totalRevenue": "إجمالي الإيرادات",
  "totalOrders": "إجمالي الطلبات",
  "activeUsers": "المستخدمون النشطون",
  "activeCaptains": "السائقون النشطون",
  "recentOrders": "الطلبات الحديثة",
  "noRecentOrders": "لا توجد طلبات حديثة",
  "orderNumber": "طلب رقم #{number}",
  "welcomeBack": "مرحباً بعودتك! إليك ما يحدث في منصة التوصيل الخاصة بك.",
  "loadingDashboardData": "جاري تحميل بيانات لوحة التحكم...",

  // Restaurant Portal
  "restaurantDashboard": "لوحة تحكم المطعم",
  "manageRestaurantOperations": "إدارة عمليات مطعمك",
  "loadingRestaurantDashboard": "جاري تحميل لوحة تحكم المطعم...",
  "noDataAvailable": "لا توجد بيانات متاحة",
  "todaysOrders": "طلبات اليوم",
  "pendingOrders": "الطلبات المعلقة",
  "tables": "الطاولات",
  "todaysRevenue": "إيرادات اليوم",
  "connected": "متصل",
  "disconnected": "غير متصل",
  "restaurantName": "اسم المطعم",

  // Landing Page
  "turboPlatform": "منصة توربو",
  "restaurantLogin": "تسجيل دخول المطعم",
  "getStarted": "ابدأ الآن",
  "adminAccess": "الوصول للمدير",
  "theComplete": "المنصة الشاملة",
  "foodDeliveryPlatform": "لتوصيل الطعام",
  "streamlineDescription": "توصيل الطعام بسهولة وفعالية",
  "startYourRestaurant": "ابدأ مطعمك",
  "threePowerfulApplications": "ثلاث تطبيقات قوية",
  "seamlessSolutions": "حلول سلسة للمطاعم والعملاء والسائقين — كل ذلك في منصة واحدة",
  "completeRestaurantManagement": "نظام إدارة مطعم شامل مع لوحة تحكم نقاط البيع المتقدمة، طلب الطاولة عبر QR، وتحليلات شاملة لاتخاذ قرارات مدروسة.",
  "advancedPOSDashboard": "لوحة تحكم نقاط البيع المتقدمة",
  "tableQROrderPlacement": "طلب الطاولة عبر QR",
  "directDeliveryOrderIntegration": "تكامل طلبات التوصيل المباشرة",
  "revenuePerformanceReports": "تقارير الإيرادات والأداء",
  "turboDriverApp": "تطبيق سائق توربو",
  "mobileApplicationForDrivers": "تطبيق محمول للسائقين لتوصيل الطعام لقبول وإدارة الطلبات، مع تأكيد التوصيل عبر رموز فريدة والملاحة في الوقت الفعلي.",
  "acceptManageOrders": "قبول وإدارة الطلبات",
  "deliveryConfirmationCodes": "رموز تأكيد التوصيل",
  "realTimeNavigation": "الملاحة في الوقت الفعلي",
  "earningsTracking": "تتبع الأرباح",
  "turboDeliveryApp": "تطبيق توصيل توربو",
  "customerFacingMobileApp": "تطبيق محمول للعملاء لتصفح قوائم المطاعم، ووضع الطلبات، وتتبع التوصيلات في الوقت الفعلي مع تجربة مستخدم سلسة.",
  "browseRestaurantMenus": "تصفح قوائم المطاعم",
  "easyOrderPlacement": "وضع الطلبات بسهولة",
  "liveDeliveryTracking": "تتبع التوصيل المباشر",
  "seamlessUserExperience": "تجربة مستخدم سلسة",
  "getItFrom": "احصل عليه من",
  "googlePlay": "جوجل بلاي",
  "appStore": "متجر التطبيقات",
  "comingSoon": "قريباً",
  "whyChooseTurboPlatform": "لماذا تختار منصة توربو؟",
  "builtForModernBusinesses": "مبنية لأعمال توصيل الطعام الحديثة مع تقنية متطورة",
  "lightningFast": "سريع كالبرق",
  "realTimeOrderProcessing": "معالجة الطلبات في الوقت الفعلي وتحديثات فورية",
  "smartAnalytics": "تحليلات ذكية",
  "dataDrivenInsights": "رؤى مدفوعة بالبيانات لتنمية أعمالك",
  "crossPlatform": "متعدد المنصات",
  "worksSeamlessly": "يعمل بسلاسة على جميع الأجهزة والمنصات",
  "support247": "دعم 24/7",
  "expertAssistance": "مساعدة خبراء متى احتجتها",
  "readyToTransform": "هل أنت مستعد لتحويل أعمال توصيل الطعام الخاصة بك؟",
  "joinThousands": "انضم إلى آلاف المطاعم التي تستخدم بالفعل منصة توربو لتنمية أعمالها",
  "startFreeTrial": "ابدأ التجربة المجانية",
  "completeFoodDeliveryPlatform": "منصة توصيل الطعام الشاملة للأعمال الحديثة.",
  "platform": "المنصة",
  "driverApp": "تطبيق السائق",
  "deliveryApp": "تطبيق التوصيل",
  "helpCenter": "مركز المساعدة",
  "connect": "تواصل",
  "contactUs": "اتصل بنا",
  "privacyPolicy": "سياسة الخصوصية",
  "termsOfService": "شروط الخدمة",
  "allRightsReserved": "© 2024 منصة توربو. جميع الحقوق محفوظة.",

  // Language Switcher
  "language": "اللغة",
  "english": "English",
  "arabic": "العربية",
  "switchLanguage": "تغيير اللغة",

  // Navigation & Menu Items
  "staffManagement": "إدارة الموظفين",
  "posSystem": "نظام نقاط البيع",
  "posOrders": "طلبات نقاط البيع",
  "deliveryOrders": "طلبات التوصيل",
  "foodPerformance": "أداء الطعام",
  "reports": "التقارير",

  // Common Actions
  "update": "تحديث",
  "create": "إنشاء",
  "remove": "إزالة",
  "select": "اختيار",
  "choose": "اختيار",
  "browse": "تصفح",
  "view": "عرض",
  "details": "التفاصيل",

  // Status & Messages
  "status": "الحالة",
  "pending": "معلق",
  "confirmed": "مؤكد",
  "preparing": "قيد التحضير",
  "ready": "جاهز",
  "delivered": "تم التوصيل",
  "cancelled": "ملغي",
  "completed": "مكتمل",
  "active": "نشط",
  "inactive": "غير نشط",
  "online": "متصل",
  "offline": "غير متصل",

  // Time & Dates
  "today": "اليوم",
  "yesterday": "أمس",
  "thisWeek": "هذا الأسبوع",
  "thisMonth": "هذا الشهر",
  "lastMonth": "الشهر الماضي",
  "thisYear": "هذا العام",
  "lastYear": "العام الماضي",
  "date": "التاريخ",
  "time": "الوقت",
  "createdAt": "تاريخ الإنشاء",
  "updatedAt": "تاريخ التحديث",

  // Financial
  "revenue": "الإيرادات",
  "cost": "التكلفة",
  "profit": "الربح",
  "total": "المجموع",
  "amount": "المبلغ",
  "price": "السعر",
  "discount": "الخصم",
  "tax": "الضريبة",
  "subtotal": "المجموع الفرعي",
  "grandTotal": "المجموع الكلي",

  // Analytics & Reports
  "overview": "نظرة عامة",
  "summary": "ملخص",
  "trends": "الاتجاهات",
  "performance": "الأداء",
  "statistics": "الإحصائيات",
  "metrics": "المقاييس",
  "insights": "الرؤى",
  "breakdown": "التفصيل",
  "comparison": "المقارنة",
  "period": "الفترة",
  "dateRange": "نطاق التاريخ",
  "customRange": "نطاق مخصص",
  "last7Days": "آخر 7 أيام",
  "last30Days": "آخر 30 يوم",
  "last90Days": "آخر 90 يوم",

  // Food & Menu
  "menu": "القائمة",
  "menuItem": "عنصر القائمة",
  "category": "الفئة",
  "description": "الوصف",
  "ingredients": "المكونات",
  "allergens": "مسببات الحساسية",
  "nutritionalInfo": "المعلومات الغذائية",
  "availability": "التوفر",
  "available": "متوفر",
  "unavailable": "غير متوفر",
  "outOfStock": "نفذ من المخزون",
  "inStock": "متوفر في المخزون",

  // Tables & Seating
  "table": "طاولة",
  "capacity": "السعة",
  "location": "الموقع",
  "notes": "ملاحظات",
  "qrCode": "رمز QR",
  "generateQR": "إنشاء رمز QR",
  "downloadQR": "تحميل رمز QR",
  "tableStatus": "حالة الطاولة",
  "tableAvailable": "متاحة",
  "occupied": "مشغولة",
  "reserved": "محجوزة",
  "maintenance": "صيانة",

  // Users & Staff
  "staff": "الموظفون",
  "employee": "الموظف",
  "role": "الدور",
  "permissions": "الصلاحيات",
  "accessLevel": "مستوى الوصول",
  "admin": "مدير",
  "manager": "مدير",
  "cashier": "كاشير",
  "waiter": "نادل",
  "kitchen": "مطبخ",

  // Forms & Inputs
  "name": "الاسم",
  "fullName": "الاسم الكامل",
  "username": "اسم المستخدم",
  "email": "البريد الإلكتروني",
  "phone": "الهاتف",
  "phoneNumber": "رقم الهاتف",
  "address": "العنوان",
  "city": "المدينة",
  "state": "الولاية",
  "country": "البلد",
  "postalCode": "الرمز البريدي",
  "zipCode": "الرمز البريدي",
  "confirmPassword": "تأكيد كلمة المرور",
  "currentPassword": "كلمة المرور الحالية",
  "newPassword": "كلمة المرور الجديدة",

  // Actions & Buttons
  "download": "تحميل",
  "upload": "رفع",
  "export": "تصدير",
  "import": "استيراد",
  "print": "طباعة",
  "share": "مشاركة",
  "copy": "نسخ",
  "paste": "لصق",

  // Empty States
  "noData": "لا توجد بيانات متاحة",
  "noResults": "لا توجد نتائج",
  "noItems": "لا توجد عناصر للعرض",
  "emptyState": "لا يوجد شيء هنا",
  "tryAgain": "حاول مرة أخرى",
  "refreshPage": "تحديث الصفحة",

  // Loading States
  "processing": "جاري المعالجة...",
  "saving": "جاري الحفظ...",
  "updating": "جاري التحديث...",
  "deleting": "جاري الحذف...",
  "connecting": "جاري الاتصال...",
  "signingIn": "جاري تسجيل الدخول...",

  // Success Messages
  "savedSuccessfully": "تم الحفظ بنجاح",
  "updatedSuccessfully": "تم التحديث بنجاح",
  "deletedSuccessfully": "تم الحذف بنجاح",
  "createdSuccessfully": "تم الإنشاء بنجاح",
  "operationSuccessful": "تم إكمال العملية بنجاح",

  // Error Messages
  "somethingWentWrong": "حدث خطأ ما",
  "tryAgainLater": "يرجى المحاولة مرة أخرى لاحقاً",
  "operationFailed": "فشلت العملية",
  "validationError": "يرجى التحقق من إدخالك",
  "requiredField": "هذا الحقل مطلوب",
  "invalidFormat": "تنسيق غير صحيح",
  "connectionError": "خطأ في الاتصال",

  // Confirmation Dialogs
  "areYouSure": "هل أنت متأكد؟",
  "thisActionCannotBeUndone": "لا يمكن التراجع عن هذا الإجراء",
  "confirmDelete": "تأكيد الحذف",
  "confirmAction": "تأكيد الإجراء",
  "proceed": "المتابعة",
  "abort": "إلغاء",

  // Restaurant Registration
  "registerYourRestaurant": "سجل مطعمك",
  "joinTurboPlatform": "انضم إلى منصة توربو ونمي أعمال توصيل الطعام الخاصة بك",
  "restaurantInformation": "معلومات المطعم",
  "ownerAccountInformation": "معلومات حساب المالك",
  "enterRestaurantName": "أدخل اسم المطعم",
  "describeRestaurant": "صف مطعمك ونوع المأكولات",
  "cuisineCategory": "فئة المأكولات",
  "selectCategory": "اختر الفئة",
  "restaurantPhone": "هاتف المطعم",
  "restaurantPhonePlaceholder": "رقم هاتف المطعم",
  "restaurantEmail": "بريد المطعم الإلكتروني",
  "restaurantEmailPlaceholder": "بريد المطعم الإلكتروني",
  "streetAddress": "عنوان الشارع",
  "streetAddressPlaceholder": "عنوان الشارع",
  "zipCodePlaceholder": "الرمز البريدي",
  "deliveryFee": "رسوم التوصيل ($)",
  "minimumOrder": "الحد الأدنى للطلب ($)",
  "estimatedDeliveryTime": "وقت التوصيل المتوقع",
  "estimatedTimePlaceholder": "مثال: 30-45 دقيقة",
  "deliveryRadius": "نطاق التوصيل (كم)",
  "enterFullName": "أدخل اسمك الكامل",
  "enterEmail": "أدخل بريدك الإلكتروني",
  "enterPhoneNumber": "أدخل رقم هاتفك",
  "enterPassword": "أدخل كلمة المرور",
  "confirmYourPassword": "أكد كلمة المرور",
  "nextOwnerInformation": "التالي: معلومات المالك",
  "backToRestaurantInfo": "العودة لمعلومات المطعم",
  "createRestaurantAccount": "إنشاء المطعم والحساب",
  "creatingAccount": "جاري إنشاء الحساب...",
  "alreadyHaveAccount": "لديك حساب بالفعل؟",
  "signInHere": "سجل دخولك هنا",
  "passwordsDoNotMatch": "كلمات المرور غير متطابقة",
  "passwordMinLength": "يجب أن تكون كلمة المرور 6 أحرف على الأقل",
  "fillAllFields": "يرجى ملء جميع حقول معلومات المطعم",
  "registrationSuccessful": "تم إنشاء المطعم والحساب بنجاح! جاري إعادة التوجيه لتسجيل الدخول...",
  "registrationFailed": "فشل التسجيل. يرجى المحاولة مرة أخرى.",

  // Food Categories
  "fastFood": "الوجبات السريعة",
  "pizza": "البيتزا",
  "asian": "آسيوي",
  "middleEastern": "شرق أوسطي",
  "desserts": "الحلويات",
  "healthy": "صحي",
  "coffee": "قهوة",
  "appetizer": "المقبلات",
  "mainDish": "الطبق الرئيسي",
  "sideDish": "الأطباق الجانبية",
  "sandwich": "ساندويتش",
  "beverage": "المشروبات",
  "drink": "مشروب",

  // Additional Food Items Page Translations
  "manageFoodItems": "إدارة قائمة أطعمة مطعمك",
  "searchFoodItems": "البحث في الأطعمة...",
  "allItems": "جميع العناصر",
  "itemsCount": "{count} من {total} عنصر",
  "noFoodItemsFound": "لا توجد أطعمة",
  "tryAdjustingFilters": "جرب تعديل المرشحات",
  "getStartedAddingFood": "ابدأ بإضافة أول طعام لك",
  "deleteFoodItemConfirm": "هل أنت متأكد من حذف هذا الطعام؟",
  "isFeatured": "مميز",
  "isPopular": "شائع",
  "originalPrice": "السعر الأصلي",
  "calories": "السعرات الحرارية",
  "protein": "البروتين",
  "carbs": "الكربوهيدرات",
  "fat": "الدهون",

  // Additional Tables Page Translations
  "manageRestaurantTables": "إدارة طاولات المطعم",
  "addTable": "إضافة طاولة",
  "totalTables": "إجمالي الطاولات",
  "allLocations": "جميع المواقع",
  "tablesCount": "{count} من {total} طاولة",
  "tableNumber": "طاولة {number}",
  "people": "أشخاص",
  "quickStatus": "الحالة السريعة",
  "noTablesFound": "لا توجد طاولات",
  "getStartedAddingTable": "ابدأ بإضافة أول طاولة لك",
  "deleteTableConfirm": "هل أنت متأكد من حذف هذه الطاولة؟",
  "downloadQRCode": "تحميل رمز QR",
  "editTable": "تعديل الطاولة",
  "deleteTable": "حذف الطاولة",
  "addNewTable": "إضافة طاولة جديدة",
  "updateTableInfo": "تحديث معلومات الطاولة",
  "createNewTableDescription": "إنشاء طاولة جديدة لمطعمك",
  "tableNumberLabel": "رقم الطاولة",
  "notesOptional": "ملاحظات (اختياري)",
  "indoor": "داخلي",
  "outdoor": "خارجي",
  "private": "خاص",
  "updateTable": "تحديث الطاولة",
  "createTable": "إنشاء الطاولة",

  // Table Order Page Translations
  "loadingMenu": "جاري تحميل القائمة...",
  "unableToLoadTable": "تعذر تحميل معلومات الطاولة. يرجى التحقق من رمز QR.",
  "addedToCart": "تمت الإضافة إلى السلة",
  "missingInformation": "معلومات ناقصة",
  "pleaseProvideNameAndPhone": "يرجى إدخال اسمك ورقم هاتفك",
  "emptyCart": "السلة فارغة",
  "pleaseAddItems": "يرجى إضافة عناصر إلى طلبك",
  "orderPlacedSuccessfully": "تم تقديم الطلب بنجاح!",
  "failedToPlaceOrder": "فشل في تقديم الطلب. يرجى المحاولة مرة أخرى.",
  "invalidQRCode": "رمز QR غير صالح",
  "tableNotAvailable": "رمز QR هذا غير صالح أو الطاولة غير متاحة.",
  "goHome": "العودة للرئيسية",
  "addToOrder": "أضف للطلب",
  "tapToViewDetails": "اضغط لعرض التفاصيل",
  "selectSize": "اختر الحجم",
  "inCart": "في السلة",
  "addMore": "أضف المزيد",
  "customerReviews": "تقييمات العملاء",
  "noReviewsYet": "لا توجد تقييمات بعد",
  "beFirstToReview": "كن أول من يقيم هذا الصنف!",
  "noItemsInCategory": "لا توجد أصناف متاحة في هذه الفئة",
  "viewCart": "عرض السلة",
  "yourOrder": "طلبك",
  "yourInformation": "معلوماتك",
  "enterYourName": "أدخل اسمك",
  "placingOrder": "جاري تقديم الطلب...",
  "placeOrder": "تقديم الطلب",
  "checkOrderStatus": "تتبع حالة الطلب",
  "enterConfirmationCode": "أدخل رمز التأكيد لتتبع طلبك",
  "confirmationCodePlaceholder": "أدخل الرمز",
  "checking": "جاري التحقق...",
  "checkStatus": "تحقق من الحالة",
  "loadingOrderStatus": "جاري تحميل حالة الطلب...",
  "orderNotFound": "الطلب غير موجود",
  "orderNotFoundDescription": "لم يتم العثور على الطلب برمز التأكيد هذا.",
  "statusUpdated": "تم تحديث الحالة",
  "orderStatusRefreshed": "تم تحديث حالة الطلب",
  "unableToFindTable": "تعذر العثور على معلومات الطاولة",
  "orderPlaced": "تم تقديم الطلب",
  "served": "تم التقديم",
  "yourOrderIsBeingPrepared": "يتم تحضير طلبك في المطبخ",
  "yourOrderIsReady": "طلبك جاهز للتقديم!",
  "enjoyYourMeal": "بالعافية!",
  "waitingForConfirmation": "في انتظار تأكيد المطعم",
  "orderConfirmedPreparing": "تم تأكيد الطلب، سيتم التحضير قريباً",
  "orderItemsDetails": "تفاصيل الطلب",
  "confirmationCode": "رمز التأكيد",
  "orderTime": "وقت الطلب",
  "orderMore": "اطلب المزيد",
  "autoRefreshNote": "يتم تحديث الحالة تلقائياً كل 30 ثانية",
  "tableOccupied": "الطاولة مشغولة",
  "tableOccupiedByAnother": "هذه الطاولة مستخدمة حالياً من قبل عميل آخر. يرجى طلب المساعدة من أحد الموظفين.",

  // Rating translations
  "howWasYourMeal": "كيف كانت وجبتك؟",
  "ratingHelpsUsImprove": "ملاحظاتك تساعدنا على تحسين خدمتنا",
  "overallExperience": "التجربة العامة",
  "serviceQuality": "جودة الخدمة",
  "rateYourItems": "قيّم أصنافك",
  "additionalComments": "تعليقات إضافية",
  "shareYourThoughts": "شاركنا أفكارك حول تجربتك...",
  "maybeLater": "ربما لاحقاً",
  "submitRating": "إرسال التقييم",
  "submitting": "جاري الإرسال...",
  "thankYouForRating": "شكراً لملاحظاتك!",
  "yourFeedbackHelps": "ملاحظاتك تساعدنا على خدمتك بشكل أفضل",
  "pleaseProvideOverallRating": "يرجى تقديم تقييم عام",
  "failedToSubmitRating": "فشل إرسال التقييم. يرجى المحاولة مرة أخرى.",
  "rateOrder": "قيّم الطلب",
  "optional": "اختياري",

  // Close Table & Receipt
  "closeTable": "إغلاق الطاولة",
  "closing": "جاري الإغلاق...",
  "tableClosedSuccessfully": "تم إغلاق الطاولة بنجاح",
  "failedToCloseTable": "فشل إغلاق الطاولة. يرجى المحاولة مرة أخرى.",
  "thankYouForVisit": "شكراً لزيارتكم!",
  "hopeToSeeYouAgain": "نتمنى رؤيتكم مرة أخرى قريباً",
  "sessionEndedMessage": "انتهت جلسة طاولتك. يمكنك بدء طلب جديد في أي وقت!",
  "redirectingIn": "إعادة التوجيه خلال",
  "seconds": "ثواني",
  "startNewOrder": "بدء طلب جديد",
  "downloadReceipt": "تحميل الفاتورة",
  "receiptDownloaded": "تم تحميل الفاتورة بنجاح",
  "failedToDownloadReceipt": "فشل تحميل الفاتورة. يرجى المحاولة مرة أخرى.",

  // Additional Orders Page Translations
  "manageTrackOrders": "إدارة وتتبع طلبات المطعم",
  "searchOrders": "البحث في الطلبات...",
  "pickedUp": "تم الاستلام",
  "onTheWay": "في الطريق",
  "allTypes": "جميع الأنواع",
  "dineIn": "تناول في المطعم",
  "newestFirst": "الأحدث أولاً",
  "oldestFirst": "الأقدم أولاً",
  "highestTotal": "الأعلى قيمة",
  "lowestTotal": "الأقل قيمة",
  "ordersCount": "{count} من {total} طلب",
  "items": "عناصر",
  "viewDetails": "عرض التفاصيل",
  "startPreparing": "بدء التحضير",
  "markReady": "وضع علامة جاهز",
  "outForDelivery": "خرج للتوصيل",
  "pickedUpStatus": "تم الاستلام",
  "servedStatus": "تم التقديم",
  "deliveredStatus": "تم التوصيل",
  "cancelOrder": "إلغاء الطلب",
  "noOrdersFound": "لا توجد طلبات",
  "ordersWillAppear": "ستظهر الطلبات هنا عندما يضعها العملاء",
  "orderDetails": "تفاصيل الطلب رقم #{number}",
  "customerInformation": "معلومات العميل",
  "orderType": "نوع الطلب",
  "deliveryAddress": "عنوان التوصيل",
  "tableInformation": "معلومات الطاولة",
  "orderItems": "عناصر الطلب",
  "quantity": "الكمية",
  "pricing": "التسعير",
  "specialInstructions": "تعليمات خاصة",
  "updateStatus": "تحديث الحالة",
  "confirmOrder": "تأكيد الطلب",
  "customizations": "التخصيصات",
  "dineInOrder": "طلب في المطعم",
  "unknownItem": "عنصر غير معروف",

  // Analytics Page Translations
  "analyticsInsights": "التحليلات والرؤى لمطعمك",
  "salesTrends": "اتجاهات المبيعات",
  "popularItems": "العناصر الشائعة",
  "customerInsights": "رؤى العملاء",
  "timeAnalysis": "تحليل الوقت",
  "orderVolume": "حجم الطلبات",
  "avgOrderValue": "متوسط قيمة الطلب",
  "customerSatisfaction": "رضا العملاء",
  "peakHours": "ساعات الذروة",
  "revenueGrowth": "نمو الإيرادات",
  "orderTrends": "اتجاهات الطلبات",
  "monthlyRevenue": "الإيرادات الشهرية",
  "dailyOrders": "الطلبات اليومية",
  "customerRetention": "الاحتفاظ بالعملاء",
  "deliveryPerformance": "أداء التوصيل",

  // Food Performance Page Translations
  "foodItemAnalytics": "تحليلات ومقاييس أداء الطعام",
  "bestSellers": "الأكثر مبيعاً",
  "lowPerformers": "الأقل أداءً",
  "profitMargins": "هوامش الربح",
  "costAnalysis": "تحليل التكلفة",
  "inventoryTurnover": "دوران المخزون",
  "seasonalTrends": "الاتجاهات الموسمية",
  "categoryPerformance": "أداء الفئة",
  "menuOptimization": "تحسين القائمة",
  "itemProfitability": "ربحية العنصر",
  "salesVelocity": "سرعة المبيعات",
  "menuEngineering": "هندسة القائمة",
  "foodCosts": "تكاليف الطعام",
  "marginAnalysis": "تحليل الهامش",

  // Reports Page Translations
  "generateCustomReports": "إنشاء وتصدير التقارير المخصصة",
  "salesReports": "تقارير المبيعات",
  "financialReports": "التقارير المالية",
  "inventoryReports": "تقارير المخزون",
  "customerReports": "تقارير العملاء",
  "operationalReports": "التقارير التشغيلية",
  "dailyReport": "التقرير اليومي",
  "weeklyReport": "التقرير الأسبوعي",
  "monthlyReport": "التقرير الشهري",
  "customReport": "تقرير مخصص",
  "exportReport": "تصدير التقرير",
  "generateReport": "إنشاء التقرير",
  "reportPeriod": "فترة التقرير",
  "startDate": "تاريخ البداية",
  "endDate": "تاريخ النهاية",
  "reportType": "نوع التقرير",
  "reportFormat": "تنسيق التقرير",
  "csvFormat": "CSV",
  "pdfFormat": "PDF",
  "excelFormat": "Excel",

  // POS System Page Translations
  "pointOfSaleSystem": "نظام نقاط البيع للطلبات داخل المطعم",
  "newOrder": "طلب جديد",
  "currentOrders": "الطلبات الحالية",
  "quickActions": "الإجراءات السريعة",
  "cashRegister": "سجل النقد",
  "cash": "نقد",
  "card": "بطاقة",
  "digitalWallet": "محفظة رقمية",
  "splitBill": "تقسيم الفاتورة",
  "addDiscount": "إضافة خصم",
  "addTip": "إضافة إكرامية",
  "voidItem": "إلغاء عنصر",
  "receipt": "إيصال",
  "emailReceipt": "إرسال الإيصال بالبريد",
  "customerDisplay": "شاشة العميل",
  "kitchenDisplay": "شاشة المطبخ",

  // POS Orders Page Translations
  "pointOfSaleOrders": "طلبات ومعاملات نقاط البيع",
  "tableOrders": "طلبات الطاولة",
  "walkInOrders": "طلبات المشي",
  "takeawayOrders": "طلبات الاستلام",
  "takeaway": "طلبات خارجية",
  "orderQueue": "قائمة انتظار الطلبات",
  "completedOrders": "الطلبات المكتملة",
  "voidedOrders": "الطلبات الملغية",
  "refundedOrders": "الطلبات المستردة",
  "transactionHistory": "تاريخ المعاملات",
  "orderTimeline": "الجدول الزمني للطلب",
  "kitchenTickets": "تذاكر المطبخ",
  "receiptHistory": "تاريخ الإيصالات",
  "dailyClosing": "الإغلاق اليومي",
  "shiftReports": "تقارير الورديات",

  // Staff Management Page Translations
  "manageRestaurantStaff": "إدارة موظفي المطعم والصلاحيات",
  "addStaff": "إضافة موظف",
  "staffList": "قائمة الموظفين",
  "workSchedule": "جدول العمل",
  "timeTracking": "تتبع الوقت",
  "payroll": "كشوف المرتبات",
  "employeeProfile": "ملف الموظف",
  "clockIn": "تسجيل الدخول",
  "clockOut": "تسجيل الخروج",
  "break": "استراحة",
  "shift": "وردية",
  "overtime": "وقت إضافي",
  "attendance": "الحضور",
  "leaves": "الإجازات",
  "holidays": "العطل",

  // Additional Settings Page Translations
  "restaurantSettings": "إعدادات وتفضيلات المطعم",
  "generalSettings": "الإعدادات العامة",
  "operationHours": "ساعات التشغيل",
  "deliverySettings": "إعدادات التوصيل",
  "paymentSettings": "إعدادات الدفع",
  "notificationSettings": "إعدادات الإشعارات",
  "taxSettings": "إعدادات الضرائب",
  "menuSettings": "إعدادات القائمة",
  "tableSettings": "إعدادات الطاولات",
  "kitchenSettings": "إعدادات المطبخ",
  "reportSettings": "إعدادات التقارير",
  "integrationSettings": "إعدادات التكامل",
  "backupSettings": "إعدادات النسخ الاحتياطي",
  "systemSettings": "إعدادات النظام",
  "advancedSettings": "الإعدادات المتقدمة",
  "saveSettings": "حفظ الإعدادات",
  "resetSettings": "إعادة تعيين الإعدادات",
  "importSettings": "استيراد الإعدادات",
  "exportSettings": "تصدير الإعدادات",
  "appearancePreferences": "المظهر والتفضيلات",
  "customizeYourInterfacePreferences": "قم بتخصيص تفضيلات واجهتك",
  "appearancePreferencesDescription": "اضبط إعدادات اللغة والسمة لتخصيص تجربتك",
  "selectYourPreferredLanguage": "اختر لغتك المفضلة",
  "switchBetweenLightAndDarkMode": "التبديل بين الوضع الفاتح والداكن",
  "theme": "السمة",
  "light": "فاتح",
  "dark": "داكن",
  "serviceFeeSettings": "إعدادات رسوم الخدمة",
  "configureServiceFeeSettingsForRestaurant": "قم بتكوين إعدادات رسوم الخدمة لمطعمك",
  "configureServiceFeeSettingsDescription": "قم بإعداد رسوم الخدمة التي سيتم تطبيقها على جميع الطلبات. تُستخدم رسوم الخدمة عادةً لتغطية التكاليف التشغيلية.",
  "enableServiceFee": "تفعيل رسوم الخدمة",
  "applyServiceFeeToAllOrders": "تطبيق رسوم الخدمة على جميع الطلبات",
  "serviceFeePercentage": "نسبة رسوم الخدمة",
  "setServiceFeeRatePercentage": "تعيين معدل رسوم الخدمة كنسبة مئوية",
  "serviceFeeEnabled": "تم تفعيل رسوم الخدمة",
  "serviceFeeDisabled": "تم تعطيل رسوم الخدمة",
  "serviceFeePercentageUpdated": "تم تحديث نسبة رسوم الخدمة إلى {percentage}%",
  "failedToUpdateServiceFee": "فشل تحديث إعداد رسوم الخدمة",
  "failedToUpdateServiceFeePercentage": "فشل تحديث نسبة رسوم الخدمة",
  "serviceFeeConfigurationActive": "تكوين رسوم الخدمة نشط",
  "serviceFeeWillBeAppliedAt": "سيتم تطبيق رسوم الخدمة بنسبة {percentage}%",

  // Restaurant Dashboard - Additional Translations
  "topSellingItems": "الأطعمة الأكثر مبيعاً (7 أيام)",
  "sold": "مُباع",
  "noSalesData": "لا توجد بيانات مبيعات متاحة",
  "revenueTrend": "اتجاه الإيرادات (7 أيام)",
  "noRevenueData": "لا توجد بيانات إيرادات متاحة",
  "hourlyPerformance": "الأداء كل ساعة",
  "avgPrepTime": "متوسط وقت التحضير",
  "min": "دقيقة",
  "completionRate": "معدل الإنجاز",
  "needAttention": "تحتاج انتباه",
  "guest": "زائر",

  // Profile Page
  "myProfile": "ملفي الشخصي",
  "manageAccountSettings": "إدارة إعدادات حسابك وتفضيلاتك",
  "personalInformation": "المعلومات الشخصية",
  "securitySettings": "إعدادات الأمان",
  "accountInformation": "معلومات الحساب",
  "bio": "السيرة الذاتية",
  "tellUsAboutYourself": "أخبرنا عن نفسك",
  "enterCurrentPassword": "أدخل كلمة المرور الحالية",
  "enterNewPassword": "أدخل كلمة المرور الجديدة",
  "confirmNewPassword": "أكد كلمة المرور الجديدة",
  "changePassword": "تغيير كلمة المرور",
  "saveChanges": "حفظ التغييرات",
  "profileUpdatedSuccessfully": "تم تحديث الملف الشخصي بنجاح",
  "profileUpdateFailed": "فشل في تحديث الملف الشخصي",
  "profileImageUpdated": "تم تحديث صورة الملف الشخصي بنجاح",
  "imageUploadFailed": "فشل في رفع الصورة",
  "pleaseSelectImage": "يرجى اختيار ملف صورة",
  "imageTooLarge": "الصورة كبيرة جداً. الحد الأقصى 5 ميجابايت",
  "passwordChangedSuccessfully": "تم تغيير كلمة المرور بنجاح",
  "passwordChangeFailed": "فشل في تغيير كلمة المرور",
  "memberSince": "عضو منذ",
  "accountType": "نوع الحساب",
  "administrator": "مدير",
  "ownerInformation": "معلومات المالك",
  "manageRestaurantProfile": "إدارة ملف مطعمك وإعداداته",
  "restaurantOwner": "مالك مطعم",

  // Loading Messages
  "loadingPOSDashboard": "جاري تحميل لوحة نقاط البيع...",
  "loadingPOSOrders": "جاري تحميل طلبات نقاط البيع...",
  "loadingPOSSystem": "جاري تحميل نظام نقاط البيع...",
  "loadingSettings": "جاري تحميل الإعدادات...",
  "loadingStaffData": "جاري تحميل بيانات الموظفين...",
  "loadingAnalytics": "جاري تحميل التحليلات...",
  "loadingPerformanceData": "جاري تحميل بيانات الأداء...",
  "loadingReports": "جاري تحميل التقارير...",
  "loadingTables": "جاري تحميل الطاولات...",
  "loadingOrders": "جاري تحميل الطلبات...",

  // Error Messages
  "failedToLoadPOSDashboard": "فشل تحميل بيانات لوحة نقاط البيع",
  "failedToLoadPOSOrders": "فشل تحميل طلبات نقاط البيع",
  "failedToLoadSettings": "فشل تحميل الإعدادات",
  "failedToLoadStaff": "فشل تحميل الموظفين",
  "failedToUpdateStatus": "فشل تحديث الحالة",
  "failedToUpdatePayment": "فشل تحديث حالة الدفع",
  "failedToCreateOrder": "فشل إنشاء طلب نقاط البيع",
  "failedToLoadTables": "فشل تحميل الطاولات",
  "failedToLoadOrders": "فشل تحميل الطلبات",
  "failedToLoadFoodItems": "فشل تحميل الأطعمة",
  "failedToLoadPaymentMethods": "فشل تحميل طرق الدفع",
  "failedToSaveSettings": "فشل حفظ الإعدادات",
  "failedToUpdateTax": "فشل تحديث إعداد الضريبة",
  "unableToLoadTables": "تعذر تحميل الطاولات",

  // Settings Success Messages
  "paymentMethodEnabled": "تم تفعيل طريقة الدفع بنجاح",
  "paymentMethodDisabled": "تم تعطيل طريقة الدفع بنجاح",
  "defaultPaymentMethodSet": "تم تعيين طريقة الدفع الافتراضية بنجاح",
  "customFeeUpdated": "تم تحديث الرسوم المخصصة بنجاح",
  "autoAcceptOrdersEnabled": "تم تفعيل قبول الطلبات تلقائياً",
  "autoAcceptOrdersDisabled": "تم تعطيل قبول الطلبات تلقائياً",
  "orderNotificationsEnabled": "تم تفعيل إشعارات الطلبات",
  "orderNotificationsDisabled": "تم تعطيل إشعارات الطلبات",
  "lowStockAlertsEnabled": "تم تفعيل تنبيهات المخزون المنخفض",
  "lowStockAlertsDisabled": "تم تعطيل تنبيهات المخزون المنخفض",
  "customerReviewsEnabled": "تم تفعيل تقييمات العملاء",
  "customerReviewsDisabled": "تم تعطيل تقييمات العملاء",
  "analyticsTrackingEnabled": "تم تفعيل تتبع التحليلات",
  "analyticsTrackingDisabled": "تم تعطيل تتبع التحليلات",
  "autoArchiveOrdersEnabled": "تم تفعيل أرشفة الطلبات تلقائياً",
  "autoArchiveOrdersDisabled": "تم تعطيل أرشفة الطلبات تلقائياً",
  "taxEnabled": "تم تفعيل الضريبة",
  "taxDisabled": "تم تعطيل الضريبة",
  "taxPercentageUpdated": "تم تحديث نسبة الضريبة إلى {percentage}%",
  "settingsSavedSuccessfully": "تم حفظ الإعدادات بنجاح",
  "setAsDefault": "تعيين كافتراضي",
  "noPaymentMethodsAvailable": "لا توجد طرق دفع متاحة",
  "contactAdministratorForPaymentMethods": "اتصل بالمسؤول لتفعيل طرق الدفع لمطعمك.",
  "minAmount": "الحد الأدنى",
  "maxAmount": "الحد الأقصى",
  "noLimit": "بدون حد",
  "automaticallyArchiveCompletedOrders": "أرشفة الطلبات المكتملة تلقائياً بعد 30 يوماً",
  "information": "معلومات",
  "importantInformationAboutPaymentMethods": "معلومات مهمة حول طرق الدفع والإعدادات",
  "paymentSecurity": "أمان الدفع",
  "allPaymentMethodsSecured": "جميع طرق الدفع آمنة ومعالجة وفقاً لمعايير الصناعة. يتم تطبيق الرسوم المخصصة بالإضافة إلى رسوم المعالجة الأساسية.",
  "feeStructure": "هيكل الرسوم",
  "customProcessingFeesDescription": "يمكنك تعيين رسوم معالجة مخصصة لكل طريقة دفع. ستُضاف هذه الرسوم إلى رسوم المنصة الأساسية.",
  "defaultPaymentMethod": "طريقة الدفع الافتراضية",
  "defaultPaymentMethodDescription": "ستكون طريقة الدفع الافتراضية محددة مسبقاً للعملاء عند وضع الطلبات من خلال مطعمك.",
  "taxConfiguration": "إعدادات الضريبة",
  "taxConfigurationDescription": "تنطبق إعدادات الضريبة على جميع الطلبات في مطعمك. عند التفعيل، سيتم حساب الضريبة وتطبيقها تلقائياً على إجمالي الطلبات.",

  // POS-Specific
  "posDashboard": "لوحة نقاط البيع",
  "posSystemOverview": "نظرة عامة على نظام نقاط البيع",
  "activeCashiers": "الكاشير النشط",
  "availableTables": "الطاولات المتاحة",
  "performanceOverview": "نظرة عامة على الأداء",
  "posTransactionsToday": "معاملات نقاط البيع اليوم",
  "totalSalesToday": "إجمالي المبيعات اليوم",
  "staffOnDuty": "الموظفون في الخدمة",
  "readyForCustomers": "جاهز للعملاء",
  "ordersWaitingToBeProcessed": "الطلبات في انتظار المعالجة",

  // POS New Order Page
  "posOrderCreatedSuccessfully": "تم إنشاء طلب نقطة البيع بنجاح",
  "newPOSOrder": "طلب نقطة بيع جديد",
  "createNewPOSOrder": "إنشاء طلب نقطة بيع جديد",
  "backToPOS": "العودة إلى نقاط البيع",
  "selectTable": "اختر الطاولة",
  "noTablesAvailable": "لا توجد طاولات متاحة",
  "ofItems": "من {count} عناصر",
  "viewTableOrders": "عرض طلبات الطاولة",
  "customerDetails": "تفاصيل العميل",
  "customerName": "اسم العميل",

  // Settings-Specific
  "orderSettings": "إعدادات الطلبات",
  "autoAcceptOrders": "قبول الطلبات تلقائياً",
  "orderNotifications": "إشعارات الطلبات",
  "lowStockAlerts": "تنبيهات المخزون المنخفض",
  "analyticsTracking": "تتبع التحليلات",
  "autoArchiveOrders": "أرشفة الطلبات تلقائياً",
  "customFee": "الرسوم المخصصة",
  "taxPercentage": "نسبة الضريبة",

  // Tables-Specific
  "tableManagement": "إدارة الطاولات",
  "manageTables": "إدارة طاولات مطعمك",
  "tableCapacity": "السعة",
  "tableLocation": "الموقع",

  // Orders-Specific
  "orderStatus": "حالة الطلب",
  "delivery": "توصيل",
  "pickup": "استلام",
  "customerInfo": "معلومات العميل",
  "totalAmount": "المبلغ الإجمالي",
  "markAsPaid": "وضع علامة كمُدفوع",
  "printOrder": "طباعة الطلب",

  // Profile
  "restaurantProfile": "الملف الشخصي للمطعم",
  "loadingProfileData": "جاري تحميل بيانات الملف الشخصي...",
  "logoUpdatedSuccessfully": "تم تحديث الشعار بنجاح",
  "logoUpdateFailed": "فشل تحديث الشعار",
  "failedToLoadRestaurantData": "فشل تحميل بيانات المطعم",

  // Performance & Reports
  "performanceMetrics": "مقاييس الأداء",
  "topPerformingItems": "العناصر الأفضل أداءً",
  "lowPerformingItems": "العناصر الأقل أداءً",
  "downloadReport": "تحميل التقرير",
  "averageOrderValue": "متوسط قيمة الطلب",
  "revenuePerCashier": "الإيرادات لكل كاشير",
  "latestTransactions": "أحدث معاملات نقاط البيع",
  "cashPayments": "المدفوعات النقدية",
  "cardPayments": "المدفوعات بالبطاقة",
  "ofTotalRevenue": "من إجمالي الإيرادات",
  "vsYesterday": "مقارنة بالأمس",
  "growth": "النمو",
  "manageInStoreOrders": "إدارة طلبات المعاملات داخل المتجر",
  "orderSummary": "ملخص الطلب",
  "orderDiscount": "خصم الطلب",
  "seats": "مقاعد",
  "noItemsInCart": "لا توجد عناصر في السلة",
  "each": "لكل",
  "selectPaymentMethod": "اختر طريقة الدفع",
  "missingPaymentMethod": "طريقة الدفع مفقودة",
  "analyticsAndReports": "التحليلات والتقارير",
  "trackRestaurantPerformance": "تتبع أداء مطعمك وإنشاء رؤى",
  "pleaseSelectPaymentMethod": "يرجى اختيار طريقة الدفع",
  "thisHour": "هذه الساعة",
  "lastHourPerformance": "أداء الساعة الماضية",
  "perOrderAverage": "متوسط لكل طلب",
  "repeatCustomers": "العملاء المتكررون",
  "orderStatusBreakdown": "تفصيل حالة الطلب",
  "distributionOfOrdersByStatus": "توزيع الطلبات حسب الحالة",
  "ordersByPaymentMethod": "الطلبات حسب طريقة الدفع",
  "mostPopularFoodItems": "الأطعمة الأكثر شعبية حسب الكمية المباعة",
  "dailyRevenueOverPeriod": "الإيرادات اليومية خلال الفترة المحددة",
  "generateReports": "إنشاء التقارير",
  "downloadDetailedReports": "تحميل تقارير مفصلة للتحليل",
  "hourlyRevenue": "الإيرادات بالساعة",
  "foodItemsPerformance": "أداء الأطعمة",
  "analyzeMenuItemsPerformance": "تحليل أداء عناصر القائمة وتحسين عروضك",
  "filtersAndSearch": "المرشحات والبحث",
  "filterAndSearchFoodItemsPerformance": "تصفية والبحث في بيانات أداء الأطعمة",
  "searchItems": "البحث في العناصر",
  "searchByName": "البحث بالاسم...",
  "quantitySold": "الكمية المباعة",
  "numberOfOrders": "عدد الطلبات",
  "from": "من",
  "unitsSold": "الوحدات المباعة",
  "ordersContainingTheseItems": "الطلبات التي تحتوي على هذه العناصر",
  "itemPerformanceDetails": "تفاصيل أداء العنصر",
  "detailedPerformanceMetrics": "مقاييس الأداء التفصيلية لكل عنصر في القائمة",
  "noItemsMatchFilters": "لا توجد عناصر تطابق المرشحات",
  "featured": "مميز",
  "popular": "شائع",
  "avgOrder": "متوسط/طلب",
  "reviews": "تقييمات",
  "reportsAndAnalytics": "التقارير والتحليلات",
  "generateAndDownloadReports": "إنشاء وتحميل تقارير مفصلة لمطعمك",
  "reportConfiguration": "إعدادات التقرير",
  "configureReportTypeAndPeriod": "تكوين نوع وفترة التقرير",
  "report": "تقرير",
  "downloadCSV": "تحميل CSV",
  "downloadJSON": "تحميل JSON",
  "revenueByOrderType": "الإيرادات حسب نوع الطلب",
  "breakdownOfRevenueByDeliveryMethod": "تفصيل الإيرادات حسب طريقة التوصيل",
  "mostPopularItemsByRevenue": "العناصر الأكثر شعبية حسب الإيرادات",
  "topCustomers": "أفضل العملاء",
  "mostValuableCustomersByTotalSpent": "أكثر العملاء قيمة حسب إجمالي الإنفاق",
  "reportInformation": "معلومات التقرير",
  "reportDetailsAndMetadata": "تفاصيل وبيانات التقرير",
  "failedToUpdatePaymentMethod": "فشل تحديث طريقة الدفع",
  "failedToSetDefaultPaymentMethod": "فشل تعيين طريقة الدفع الافتراضية",
  "failedToUpdateCustomFee": "فشل تحديث الرسوم المخصصة",
  "failedToUpdateSetting": "فشل تحديث الإعداد",
  "failedToUpdateTaxPercentage": "فشل تحديث نسبة الضريبة",
  "configureRestaurantPreferences": "تكوين تفضيلات مطعمك وطرق الدفع",
  "generatedAt": "تم الإنشاء في",
  "configureTaxSettingsForRestaurant": "تكوين إعدادات الضريبة لمطعمك",
  "configureTaxSettingsDescription": "تكوين إعدادات الضريبة لمطعمك. سيتم تطبيق الضريبة على جميع الطلبات عند التمكين.",
  "enableTax": "تفعيل الضريبة",
  "applyTaxToAllOrders": "تطبيق الضريبة على جميع الطلبات",
  "setTaxRatePercentage": "تعيين نسبة الضريبة",
  "taxConfigurationActive": "إعدادات الضريبة نشطة",
  "taxWillBeAppliedAt": "سيتم تطبيق الضريبة بنسبة {percentage}% على جميع الطلبات. سيظهر هذا في نظام نقاط البيع وطلبات العملاء.",
  "configurePaymentMethodsForCustomers": "تكوين طرق الدفع المتاحة لعملائك",
  "configurePaymentMethodsDescription": "تكوين طرق الدفع المتاحة لعملائك. يمكنك تعيين رسوم معالجة مخصصة وحدود لكل طريقة.",
  "default": "افتراضي",
  "baseProcessingFee": "رسوم المعالجة الأساسية",
  "contactAdministratorToEnable": "اتصل بالمسؤول لتمكين طرق الدفع لمطعمك.",
  "configureGeneralRestaurantSettings": "تكوين إعدادات وتفضيلات المطعم العامة",
  "configureGeneralRestaurantSettingsDescription": "تكوين إعدادات وتفضيلات المطعم العامة.",
  "automaticallyAcceptIncomingOrders": "قبول الطلبات الواردة تلقائياً",
  "receiveNotificationsForNewOrders": "تلقي إشعارات للطلبات الجديدة",
  "getNotifiedWhenItemsLowInStock": "الحصول على إشعار عندما تكون العناصر منخفضة في المخزون",
  "allowCustomersToLeaveReviews": "السماح للعملاء بترك التقييمات",
  "trackCustomerBehaviorAndAnalytics": "تتبع سلوك العملاء والتحليلات",
  "failedToUpdateStaffStatus": "فشل تحديث حالة الموظف",
  "failedToDeleteStaffMember": "فشل حذف الموظف",
  "failedToUpdateStaffMember": "فشل تحديث الموظف",
  "failedToCreateStaffMember": "فشل إنشاء الموظف",
  "manageRestaurantStaffMembers": "إدارة موظفي المطعم وصلاحياتهم",
  "addStaffMember": "إضافة موظف",
  "totalStaff": "إجمالي الموظفين",
  "allTeamMembers": "جميع أعضاء الفريق",
  "activeStaff": "الموظفون النشطون",
  "currentlyActive": "نشط حالياً",
  "managers": "المديرون",
  "adminAndManagerRoles": "أدوار المدير والمسؤول",
  "deactivatedAccounts": "الحسابات المعطلة",
  "filterAndSearchStaffMembers": "تصفية والبحث عن الموظفين",
  "searchStaffMembers": "البحث عن الموظفين...",
  "filterByRole": "تصفية حسب الدور",
  "filterByStatus": "تصفية حسب الحالة",
  "allRoles": "جميع الأدوار",
  "allStatus": "جميع الحالات",
  "applyFilters": "تطبيق المرشحات",
  "staffMembers": "الموظفون",
  "ofStaffMembers": "من {total} موظف",
  "noStaffMembersFound": "لم يتم العثور على موظفين",
  "tryAdjustingYourFilters": "حاول تعديل المرشحات",
  "getStartedByAddingFirstStaffMember": "ابدأ بإضافة أول موظف",
  "editStaffMember": "تعديل الموظف",
  "addNewStaffMember": "إضافة موظف جديد",
  "firstName": "الاسم الأول",
  "lastName": "اسم العائلة",
  "kitchenStaff": "طاقم المطبخ",
  "staffMemberActivated": "تم تفعيل الموظف بنجاح",
  "staffMemberDeactivated": "تم إلغاء تفعيل الموظف بنجاح",
  "staffMemberDeleted": "تم حذف الموظف بنجاح",
  "staffMemberUpdated": "تم تحديث الموظف بنجاح",
  "staffMemberCreated": "تم إنشاء الموظف بنجاح",
  "areYouSureDeleteStaffMember": "هل أنت متأكد من حذف هذا الموظف؟",
  "joined": "انضم",
  "lastLogin": "آخر تسجيل دخول",
  "canProcessOrders": "يمكن معالجة الطلبات",
  "canManageInventory": "يمكن إدارة المخزون",
  "canManageUsers": "يمكن إدارة المستخدمين",
  "canViewReports": "يمكن عرض التقارير",
  "canManageTables": "يمكن إدارة الطاولات",
  "canManageMenu": "يمكن إدارة القائمة",
  "canProcessRefunds": "يمكن معالجة الاستردادات",
  "canViewAnalytics": "يمكن عرض التحليلات",
  "accessDenied": "تم رفض الوصول",
  "insufficientPermissions": "ليس لديك الصلاحيات الكافية للوصول إلى هذه الصفحة.",
  "contactAdministratorForAccess": "يرجى الاتصال بالمسؤول لطلب الوصول.",
  "requiredPermissions": "الصلاحيات المطلوبة",
  "notSet": "غير محدد",
  "editProfile": "تعديل الملف الشخصي",
  "manageYourPersonalInformation": "إدارة معلوماتك الشخصية وإعدادات الحساب",
  "activePermissions": "نشط",
  "passwordRequired": "كلمة المرور مطلوبة",
  "passwordUpdatedSuccessfully": "تم تحديث كلمة المرور بنجاح",
  "failedToUpdatePassword": "فشل تحديث كلمة المرور",
  "updatePassword": "تحديث كلمة المرور",
  "staffLogin": "تسجيل دخول الموظفين",
  "signInAsStaffMember": "تسجيل الدخول كموظف",
  "restaurantId": "معرف المطعم",
  "enterRestaurantId": "أدخل معرف المطعم",
  "loginAsOwner": "تسجيل الدخول كمالك",
  "staffMember": "موظف",
  "loginAsStaff": "تسجيل الدخول كموظف",
  "currentOrder": "الطلب الحالي",
  "ordersWillAppearHere": "ستظهر الطلبات هنا عندما يضعها العملاء",
  "getStartedByAddingFirstTable": "ابدأ بإضافة طاولتك الأولى",
  "printReceipt": "طباعة الإيصال",
  "revenuePerformance": "أداء الإيرادات",
  "quantityPerformance": "أداء الكمية",
  "rating": "التقييم",
  "orderPerformance": "أداء الطلبات",
  "updateStaffMember": "تحديث الموظف",

  // Kitchen Orders Page
  "kitchenOrders": "طلبات المطبخ",
  "kitchenOrdersDescription": "إدارة وتحضير الطلبات المؤكدة",
  "waitingToStart": "في انتظار البدء",
  "currentlyPreparing": "قيد التحضير حالياً",
  "noOrdersInKitchen": "لا توجد طلبات في المطبخ",
  "orderStatusUpdated": "تم تحديث حالة الطلب",
  "totalItems": "إجمالي الأصناف",
  "activeOrders": "الطلبات النشطة",
  "ordersRequiringAttention": "الطلبات التي تتطلب انتباهك",

  // Waiter Orders Page
  "waiterOrders": "طلبات النادل",
  "waiterOrdersDescription": "تقديم الطلبات الجاهزة للعملاء",
  "readyToServe": "جاهز للتقديم",
  "noReadyOrders": "لا توجد طلبات جاهزة",
  "readyOrdersWillAppear": "ستظهر الطلبات الجاهزة هنا",
  "markServed": "وضع علامة تم التقديم",
  "returnOrder": "إرجاع الطلب",
  "returnReason": "سبب الإرجاع",
  "enterReturnReason": "أدخل سبب إرجاع هذا الطلب",
  "confirmReturn": "تأكيد الإرجاع",
  "orderReturned": "تم إرجاع الطلب بنجاح",
  "orderServed": "تم وضع علامة تم التقديم",
  "pleaseProvideReturnReason": "يرجى تقديم سبب لإرجاع الطلب",
  "minutesAgo": "دقائق مضت",
  "hoursAgo": "ساعات مضت",
  "more": "المزيد",
  "returned": "مُرجع",

  // POS New Order Page
  "tapToSelectSize": "اضغط لاختيار الحجم",
  "tapToAdd": "اضغط للإضافة",
  "searchTableNumber": "ابحث عن رقم الطاولة...",
  "noItemsFound": "لم يتم العثور على أصناف",
  "noItemsSelected": "لم يتم اختيار أصناف",
  "selected": "المحدد",

  // Delivery Orders Page
  "manageDeliveryOrders": "إدارة جميع طلبات التوصيل",
  "fromTurboApp": "من تطبيق تيربو",
  "allDeliverySources": "جميع المصادر",
  "appOrder": "طلب من التطبيق",
  "webOrder": "طلب من الويب",
  "loadingDeliveryOrders": "جاري تحميل طلبات التوصيل...",
  "failedToLoadDeliveryOrders": "فشل في تحميل طلبات التوصيل",
  "noDeliveryOrdersFound": "لا توجد طلبات توصيل",
  "deliveryOrdersWillAppear": "ستظهر طلبات التوصيل من تطبيق تيربو هنا",
  "totalDeliveryOrders": "إجمالي طلبات التوصيل",
  "fromDeliveryOrders": "من طلبات التوصيل",
  "driver": "السائق",
  "noAddress": "لم يتم تقديم عنوان",
  "customer": "العميل",

  // Customer Management Page
  "customers": "العملاء",
  "manageCustomers": "إدارة العملاء ومعلوماتهم",
  "totalCustomers": "إجمالي العملاء",
  "activeCustomers": "العملاء النشطون",
  "phoneOrderCustomers": "عملاء الطلبات الهاتفية",
  "appCustomers": "عملاء التطبيق",
  "quickPhoneSearch": "بحث سريع بالهاتف",
  "searchCustomers": "البحث عن العملاء...",
  "allSources": "جميع المصادر",
  "mobileApp": "تطبيق الجوال",
  "phoneOrder": "طلب هاتفي",
  "web": "الويب",
  "totalSpent": "إجمالي الإنفاق",
  "lastOrder": "آخر طلب",
  "never": "أبداً",
  "showingOf": "عرض {count} من {total}",
  "customerNotFound": "العميل غير موجود",
  "noCustomerWithPhone": "لم يتم العثور على عميل بهذا الرقم",
  "searchFailed": "فشل البحث",
  "noCustomersFound": "لم يتم العثور على عملاء",
  "failedToLoadCustomers": "فشل في تحميل العملاء",

  // Customer Detail Modal
  "registrationSource": "مصدر التسجيل",
  "adminNotes": "ملاحظات المدير",
  "addNotesAboutCustomer": "أضف ملاحظات حول هذا العميل...",
  "noNotesYet": "لا توجد ملاحظات بعد",
  "notesUpdated": "تم تحديث الملاحظات",
  "failedToUpdateNotes": "فشل في تحديث الملاحظات",
  "noSavedAddresses": "لا توجد عناوين محفوظة",
  "setDefault": "تعيين كافتراضي",
  "addressDeleted": "تم حذف العنوان",
  "failedToDeleteAddress": "فشل في حذف العنوان",
  "defaultAddressUpdated": "تم تحديث العنوان الافتراضي",
  "failedToUpdateAddress": "فشل في تحديث العنوان",
  "noOrderHistory": "لا يوجد سجل طلبات",
  "unknownRestaurant": "مطعم غير معروف",
  "favoriteRestaurants": "المطاعم المفضلة",
  "noFavoriteRestaurants": "لا توجد مطاعم مفضلة",
  "favoriteFoodItems": "الأطعمة المفضلة",
  "noFavoriteFoodItems": "لا توجد أطعمة مفضلة",

  // Live Order Map
  "liveOrderMap": "خريطة الطلبات المباشرة",
  "failedToLoadMapData": "فشل في تحميل بيانات الخريطة",
  "googleMapsApiKeyMissing": "مفتاح Google Maps API مفقود",
  "failedToLoadGoogleMaps": "فشل في تحميل خرائط جوجل",
  "exitFullscreen": "الخروج من ملء الشاشة",
  "fullscreen": "ملء الشاشة",

  // Phone Order Wizard
  "newPhoneOrder": "طلب هاتفي جديد",
  "step": "الخطوة",
  "of": "من",
  "customerPhone": "هاتف العميل",
  "enterValidPhone": "يرجى إدخال رقم هاتف صالح",
  "existingCustomer": "عميل حالي",
  "newCustomer": "عميل جديد",
  "enterCustomerName": "أدخل اسم العميل",
  "selectAddress": "اختر العنوان",
  "useSavedAddress": "استخدام عنوان محفوظ",
  "addNewAddress": "إضافة عنوان جديد",
  "home": "المنزل",
  "work": "العمل",
  "other": "آخر",
  "fullAddressOptional": "العنوان الكامل (اختياري)",
  "deliveryNotes": "ملاحظات التوصيل (اختياري)",
  "searchRestaurants": "البحث عن المطاعم...",
  "all": "الكل",
  "cart": "السلة",
  "assignDriver": "تعيين سائق",
  "autoAssign": "تعيين تلقائي",
  "systemWillAssign": "سيقوم النظام بتعيين سائق متاح",
  "anySpecialInstructions": "أي تعليمات خاصة لهذا الطلب؟",
  "orderCreatedSuccessfully": "تم إنشاء الطلب بنجاح",
  "dropPinOnMap": "ضع دبوسًا على الخريطة لتحديد موقع التوصيل",
  "loadingMap": "جاري تحميل الخريطة...",
  "locationSelected": "تم تحديد الموقع",
  "clickOrDragMarker": "انقر على الخريطة أو اسحب العلامة لتحديد الموقع",
  "noCoordinates": "لا توجد إحداثيات - حدد على الخريطة",
  "addressFromMap": "العنوان (يتم ملؤه تلقائيًا من الخريطة)",

  // Additional Admin Delivery Orders
  "captainAssigned": "تم تعيين السائق بنجاح",
  "failedToAssignCaptain": "فشل في تعيين السائق",
  "noAvailableDrivers": "لا يوجد سائقون متاحون",
  "order": "الطلب",
  "actions": "الإجراءات",
  "awaitingDriver": "في انتظار السائق",
  "note": "ملاحظة",
  "successfullyDelivered": "تم التوصيل بنجاح",
  "awaitingConfirmation": "في انتظار التأكيد",

  // Session Expiry
  "sessionExpired": "انتهت الجلسة",
  "sessionExpiredDesc": "لقد انتهت صلاحية جلستك والرمز الخاص بك. يرجى تسجيل الدخول مرة أخرى لمواصلة استخدام التطبيق بأمان.",
  "logInAgain": "تسجيل الدخول مرة أخرى",

  // Menu Book
  "menuBook": "دليل المطاعم",
  "browseRestaurants": "تصفح المطاعم",
  "searchRestaurants": "ابحث عن مطاعم...",
  "allCategories": "الكل",
  "currentlyOpen": "مفتوح",
  "currentlyClosed": "مغلق",
  "viewRestaurantMenu": "عرض القائمة",
  "noRestaurantsFound": "لم يتم العثور على مطاعم",
  "tryDifferentSearch": "جرّب بحثاً مختلفاً أو فئة أخرى",
  "restaurantMenu": "القائمة",
  "operatingHoursTitle": "ساعات العمل",
  "deliveryMinimum": "الحد الأدنى للتوصيل",
  "contactInfo": "معلومات الاتصال",
  "restaurantInfo": "معلومات المطعم",
  "itemUnavailable": "غير متوفر",
  "allItems": "الكل",
  "featuredItems": "مميز",
  "popularItems": "شائع",
  "noMenuItems": "لا توجد أصناف في القائمة",
  "noItemsInCategory": "لا توجد أصناف في هذه الفئة",
  "prepTime": "وقت التحضير",
  "ingredients": "المكونات",
  "allergens": "مسببات الحساسية",
  "nutritionalInfo": "القيمة الغذائية",
  "calories": "السعرات الحرارية",
  "protein": "البروتين",
  "carbs": "الكربوهيدرات",
  "fat": "الدهون",
  "poweredByTurbo": "بدعم من توربو ديليفري",
  "monday": "الإثنين",
  "tuesday": "الثلاثاء",
  "wednesday": "الأربعاء",
  "thursday": "الخميس",
  "friday": "الجمعة",
  "saturday": "السبت",
  "sunday": "الأحد",
  "today": "اليوم",
  "closed": "مغلق",
  "fastFood": "وجبات سريعة",
  "pizza": "بيتزا",
  "asian": "آسيوي",
  "middleEastern": "شرق أوسطي",
  "desserts": "حلويات",
  "healthy": "صحي",
  "coffee": "قهوة",
  "appetizer": "مقبلات",
  "mainDish": "طبق رئيسي",
  "sideDish": "طبق جانبي",
  "sandwich": "ساندويتش",
  "beverage": "مشروب",
  "drink": "مشروب",
}
